import {featureEach} from '@turf/meta';
import {Evented, FullscreenControl, LngLat, Map as Mapbox, MercatorCoordinate, NavigationControl, ScaleControl} from 'mapbox-gl';
import AnimatedPopup from 'mapbox-gl-animated-popup';
import animation from './animation';
import Clock from './clock';
import configs from './configs';
import {ClockControl, MapboxGLButtonControl, SearchControl} from './controls';
import Dataset from './dataset';
import {Airport, Flight, FlightStatus, Operator, POI, RailDirection, Railway, SafieDevice, IchimillDevice, KoujiLocation, Station, Train, TrainTimetables, TrainType, TrainVehicleType} from './data-classes';
import extend from './extend';
import {
    buildEarthquakeButtonSummary,
    escapeHTML,
    getEarthquakeIntensityColor,
    getEarthquakeIntensityRank
} from './helpers/helpers-earthquake';
import {
    buildTyphoonButtonSummary
} from './helpers/helpers-typhoon';
import {
    buildTsunamiButtonSummary,
    getTsunamiMarkerFillColor,
    getTsunamiWarningBorderColor,
    mergeTsunamiStationsByLatestObservation
} from './helpers/helpers-tsunami';
import * as helpers from './helpers/helpers';
import {pickObject} from './helpers/helpers-deck';
import * as helpersGeojson from './helpers/helpers-geojson';
import * as helpersMapbox from './helpers/helpers-mapbox';
import {GeoJsonLayer, ThreeLayer, Tile3DLayer, TrafficLayer} from './layers';
import {
    loadBusData,
    loadDynamicBusData,
    loadDynamicFlightData,
    loadDynamicTrainData,
    loadSafieDevices,
    loadIchimillDevices,
    loadStaticData,
    loadTimetableData,
    startEarthquakeUpdates,
    startTyphoonUpdates,
    startTsunamiUpdates,
    startSafieDeviceUpdates,
    stopSafieDeviceUpdates,
    startIchimillDeviceUpdates,
    stopIchimillDeviceUpdates,
    startKoujiLocationUpdates,
    stopKoujiLocationUpdates,
    updateOdptUrl
} from './loader';
import Marker from './marker';
import {AboutPanel, BusPanel, EarthquakePanel, LayerPanel, LocationPanel, SharePanel, StationPanel, TrackingModePanel, TrainPanel, TsunamiPanel, TyphoonPanel} from './panels';
import Plugin from './plugin';
import nearestCloserPointOnLine from './turf/nearest-closer-point-on-line';

const RAILWAY_NAMBOKU = 'TokyoMetro.Namboku',
    RAILWAY_MITA = 'Toei.Mita',
    RAILWAY_ARAKAWA = 'Toei.Arakawa';

const AIRLINES_FOR_ANA_CODE_SHARE = ['ADO', 'SFJ', 'SNJ'];

const DEGREE_TO_RADIAN = Math.PI / 180;
const TSUNAMI_REFRESH_INTERVAL = 60 * 1000;
const EARTHQUAKE_REFRESH_INTERVAL = 60 * 1000;
const TYPHOON_REFRESH_INTERVAL = 60 * 1000;
const TSUNAMI_NEW_WINDOW = 24 * 60 * 60 * 1000;
const EARTHQUAKE_NEW_WINDOW = 24 * 60 * 60 * 1000;
const DISASTER_SELECT_ALL = '__all__';
const DISASTER_OVERVIEW_CENTER = [137.2, 31.7];
const DISASTER_OVERVIEW_ZOOM = 4.7;
const TYPHOON_SOURCE_ID = 'eqcare-typhoon-source';
const TYPHOON_STRONG_WIND_FILL_LAYER_ID = 'eqcare-typhoon-strong-wind-fill';
const TYPHOON_STRONG_WIND_LINE_LAYER_ID = 'eqcare-typhoon-strong-wind-line';
const TYPHOON_STORM_AREA_FILL_LAYER_ID = 'eqcare-typhoon-storm-area-fill';
const TYPHOON_STORM_AREA_LINE_LAYER_ID = 'eqcare-typhoon-storm-area-line';
const TYPHOON_STORM_WARNING_LINE_LAYER_ID = 'eqcare-typhoon-storm-warning-line';
const TYPHOON_PROBABILITY_OUTLINE_LAYER_ID = 'eqcare-typhoon-probability-outline';
const TYPHOON_PROBABILITY_CIRCLE_LAYER_ID = 'eqcare-typhoon-probability-circle';
const TYPHOON_TRACK_LAYER_ID = 'eqcare-typhoon-track';
const TYPHOON_FORECAST_POINT_LAYER_ID = 'eqcare-typhoon-forecast-point';
const TYPHOON_CURRENT_POINT_LAYER_ID = 'eqcare-typhoon-current-point';
const TYPHOON_LABEL_LAYER_ID = 'eqcare-typhoon-label';

function createEmptyTyphoonFeatureCollection() {
    return {
        type: 'FeatureCollection',
        features: []
    };
}

function isDisasterSelectAll(value) {
    return value === DISASTER_SELECT_ALL;
}

function mergeTyphoonFeatureCollections(typhoons = []) {
    return {
        type: 'FeatureCollection',
        features: typhoons.flatMap(typhoon => typhoon?.mapFeatureCollection?.features || [])
    };
}

function getInitialDisplayTargetTyphoons(typhoons = []) {
    return typhoons.filter(typhoon => typhoon?.isInitialDisplayTarget);
}

function collectCoordinatesFromGeometry(geometry, coordinates = []) {
    if (!geometry || !geometry.coordinates) {
        return coordinates;
    }

    const appendCoordinate = value => {
        if (Array.isArray(value) && value.length >= 2 && Number.isFinite(value[0]) && Number.isFinite(value[1])) {
            coordinates.push([value[0], value[1]]);
            return;
        }
        if (Array.isArray(value)) {
            value.forEach(appendCoordinate);
        }
    };

    appendCoordinate(geometry.coordinates);
    return coordinates;
}

// Replace NavigationControl._updateZoomButtons to support disabling the control
const _updateZoomButtons = NavigationControl.prototype._updateZoomButtons;
NavigationControl.prototype._updateZoomButtons = function() {
    const me = this,
        {_disabled, _zoomInButton, _zoomOutButton, _compass} = me;

    _updateZoomButtons.apply(me);

    if (_disabled) {
        _zoomInButton.disabled = _zoomOutButton.disabled = true;
        _zoomInButton.setAttribute('aria-disabled', 'true');
        _zoomOutButton.setAttribute('aria-disabled', 'true');
    }
    _compass.disabled = !!_disabled;
    _compass.setAttribute('aria-disabled', (!!_disabled).toString());
};

NavigationControl.prototype.enable = function() {
    const me = this;

    delete me._disabled;
    me._updateZoomButtons();
};

NavigationControl.prototype.disable = function() {
    const me = this;

    me._disabled = true;
    me._updateZoomButtons();
};

const modelOrigin = MercatorCoordinate.fromLngLat(configs.defaultCenter);
const modelScale = modelOrigin.meterInMercatorCoordinateUnits();

export default class extends Evented {

    constructor(options) {
        super();

        const me = this;

        options = extend({
            hash: false,
            useWebGL2: true,
            center: configs.defaultCenter,
            zoom: configs.defaultZoom,
            bearing: configs.defaultBearing,
            pitch: configs.defaultPitch,
            dataUrl: configs.dataUrl,
            dataSources: configs.dataSources,
            clockControl: true,
            searchControl: true,
            navigationControl: true,
            fullscreenControl: true,
            modeControl: true,
            configControl: true,
            trackingMode: configs.defaultTrackingMode,
            ecoMode: configs.defaultEcoMode,
            ecoFrameRate: configs.defaultEcoFrameRate
        }, options);

        me.lang = helpers.getLang(options.lang);
        me.dataUrl = options.dataUrl;
        me.dataSources = options.dataSources.map(({gtfsUrl, vehiclePositionUrl, color}) => ({
            gtfsUrl: updateOdptUrl(gtfsUrl, options.secrets),
            vehiclePositionUrl: updateOdptUrl(vehiclePositionUrl, options.secrets),
            color
        }));
        me.container = typeof options.container === 'string' ?
            document.getElementById(options.container) : options.container;
        me.secrets = options.secrets;
        me.exitPopups = [];
        me._initialDisasterFetchState = {
            earthquake: !configs.apiUrl.earthquake,
            tsunami: !configs.apiUrl.tsunami,
            typhoon: !configs.apiUrl.typhoon
        };
        me._initialDisasterDisplayResolved = false;

        me.clockControl = options.clockControl;
        me.searchControl = options.searchControl;
        me.navigationControl = options.navigationControl;
        me.fullscreenControl = options.fullscreenControl;
        me.modeControl = options.modeControl;
        me.configControl = options.configControl;
        me.clock = new Clock();
        me.plugins = (options.plugins || []).map(plugin => new Plugin(plugin));

        me.searchMode = 'none';
        me.viewMode = configs.defaultViewMode;
        me.trackingMode = options.trackingMode;
        me.trackingParams = {
            zoom: {},
            bearing: {},
            pitch: {}
        };
        me.lastCameraParams = {};
        me.typhoonCameraRestoreState = null;
        me.initialSelection = options.selection;
        me.clockMode = configs.defaultClockMode;
        me.isEditingTime = false;
        me.ecoMode = options.ecoMode;
        me.ecoFrameRate = options.ecoFrameRate;

        me.lastDynamicUpdate = {};
        me.lastRepaint = 0;
        me.frameRateFactor = 1;

        // The inner map container overrides the option
        options.container = initContainer(me.container);

        // This style overrides the option
        options.style = `${options.dataUrl}/osm-liberty.json`;

        // The custom attribution will be appended only if ConfigControl is visible
        if (!options.configControl) {
            options.customAttribution = helpers.flat([options.customAttribution, configs.customAttribution]);
        }

        me.map = new Mapbox(options);

        for (const event of configs.events) {
            me.map.on(event, me.fire.bind(me));
        }

        me.map.once('idle', () => {
            helpers.measureFrameRate().then(frameRate => {
                me.frameRateFactor = Math.min(60 / frameRate, 2);
            });
        });

        Promise.all([
            loadStaticData(me.dataUrl, me.lang, me.clock)
                .then(me.initData.bind(me))
                .catch(error => {
                    showErrorMessage(me.container);
                    throw error;
                }),
            new Promise(resolve => {
                me.map.once('styledata', resolve);
            })
        ]).then(me.initialize.bind(me));

        // リアルタイム公共交通データ更新の停止
        // me.refreshBusData();
    }

    /**
     * Returns the Mapbox's Map object used in the map.
     * @returns {mapboxgl.Map} The Mapbox's Map
     */
    getMapboxMap() {
        return this.map;
    }

    /**
     * Returns the map's geographical centerpoint.
     * @returns {LngLat} The map's geographical centerpoint
     */
    getCenter() {
        return this.map.getCenter();
    }

    /**
     * Sets the map's geographical centerpoint. Equivalent to jumpTo({center: center}).
     * @param {LngLatLike} center - The centerpoint to set
     * @returns {Map} Returns itself to allow for method chaining
     */
    setCenter(center) {
        const me = this;

        me.trackObject();
        me.map.setCenter(center);
        return me;
    }

    /**
     * Returns the map's current zoom level.
     * @returns {number} The map's current zoom level
     */
    getZoom() {
        return this.map.getZoom();
    }

    /**
     * Sets the map's zoom level. Equivalent to jumpTo({zoom: zoom}).
     * @param {number} zoom - The zoom level to set (0-20)
     * @returns {Map} Returns itself to allow for method chaining
     */
    setZoom(zoom) {
        const me = this;

        me.map.setZoom(zoom);
        return me;
    }

    /**
     * Returns the map's current bearing. The bearing is the compass direction that
     * is "up"; for example, a bearing of 90° orients the map so that east is up.
     * @returns {number} The map's current bearing
     */
    getBearing() {
        return this.map.getBearing();
    }

    /**
     * Sets the map's bearing (rotation). The bearing is the compass direction that
     * is "up"; for example, a bearing of 90° orients the map so that east is up.
     * Equivalent to jumpTo({bearing: bearing}).
     * @param {number} bearing - The desired bearing
     * @returns {Map} Returns itself to allow for method chaining
     */
    setBearing(bearing) {
        const me = this;

        me.trackObject();
        me.map.setBearing(bearing);
        return me;
    }

    /**
     * Returns the map's current pitch (tilt).
     * @returns {number} The map's current pitch, measured in degrees away from the
     *     plane of the screen
     */
    getPitch() {
        return this.map.getPitch();
    }

    /**
     * Sets the map's pitch (tilt). Equivalent to jumpTo({pitch: pitch}).
     * @param {number} pitch - The pitch to set, measured in degrees away from the
     *     plane of the screen (0-60)
     * @returns {Map} Returns itself to allow for method chaining
     */
    setPitch(pitch) {
        const me = this;

        me.map.setPitch(pitch);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, pitch, and padding with an
     * animated transition between old and new values. The map will retain its current
     * values for any details not specified in options.
     * @param {Object} options - Options describing the destination and animation of
     *     the transition. Accepts CameraOptions and AnimationOptions
     * @returns {Map} Returns itself to allow for method chaining
     */
    easeTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.easeTo(options);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, and pitch, animating the
     * transition along a curve that evokes flight. The animation seamlessly incorporates
     * zooming and panning to help the user maintain her bearings even after traversing
     * a great distance.
     * @param {Object} options - Options describing the destination and animation of
     *     the transition. Accepts CameraOptions, AnimationOptions, and a few additional
     *     options
     * @returns {Map} Returns itself to allow for method chaining
     */
    flyTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.flyTo(options);
        return me;
    }

    /**
     * Changes any combination of center, zoom, bearing, and pitch, without an animated
     * transition. The map will retain its current values for any details not specified
     * in options.
     * @param {CameraOptions} options - Options object
     * @returns {Map} Returns itself to allow for method chaining
     */
    jumpTo(options) {
        const me = this;

        if (options.center !== undefined || options.bearing !== undefined) {
            me.trackObject();
        }
        me.map.jumpTo(options);
        return me;
    }

    /**
     * Returns the current view mode.
     * @returns {string} Current view mode: 'ground' or 'underground'
     */
    getViewMode() {
        return this.viewMode;
    }

    /**
     * Sets the view mode.
     * @param {string} mode - View mode: 'ground' or 'underground'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setViewMode(mode) {
        const me = this;

        if (me.initialized) {
            me._setViewMode(mode);
        }
        return me;
    }

    /**
     * Returns the current tracking mode.
     * @returns {string} Current tracking mode: 'position', 'back', 'topback', 'front',
     *     'topfront', 'helicopter', 'drone', 'bird' or 'heading'
     */
    getTrackingMode() {
        return this.trackingMode;
    }

    /**
     * Sets the tracking mode.
     * @param {string} mode - Tracking mode: 'position', 'back', 'topback', 'front',
     *     'topfront', 'helicopter', 'drone', 'bird' or 'heading'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setTrackingMode(mode) {
        const me = this;

        me._setTrackingMode(mode);
        return me;
    }

    /**
     * Returns the ID of the train or flight being tracked, or the IDs of the selected
     * stations.
     * @returns {string | Array<string>} The ID of the train or flight being tracked, or
     *     the IDs of the selected stations
     */
    getSelection() {
        const trackedObject = this.trackedObject;

        if (isVehicle(trackedObject)) {
            return trackedObject.object.id;
        } else if (isStation(trackedObject)) {
            return trackedObject.stations;
        }
    }

    /**
     * Sets the ID of the train or flight you want to track, or the station to select.
     * @param {string} id - ID of the train or flight to be tracked, or the station to
     *     be selected
     * @returns {Map} Returns itself to allow for method chaining
     */
    setSelection(id) {
        const me = this,
            selection = helpers.removePrefix(id),
            station = me.stations.get(selection);

        if (station) {
            me.trackObject(me.stationGroupLookup.get(station.group));
            delete me.initialSelection;
        } else if (!selection.match(/NRT|HND/)) {
            if (me.trainLoaded) {
                let activeTrain;

                for (const id of me.timetables.getConnectingTrainIds(selection)) {
                    if ((activeTrain = me.activeTrainLookup.get(id))) {
                        break;
                    }
                }
                if (activeTrain) {
                    if (activeTrain.cars[0]) {
                        me.trackObject(activeTrain.cars[0]);
                    } else {
                        me.selection = activeTrain.id;
                    }
                } else {
                    helpers.showNotification(me.container, me.dict['train-terminated']);
                }
                delete me.initialSelection;
            } else {
                me.initialSelection = selection;
            }
        } else {
            if (me.flightLoaded) {
                const activeFlight = me.activeFlightLookup.get(selection);

                if (activeFlight) {
                    if (activeFlight.aircraft) {
                        me.trackObject(activeFlight.aircraft);
                    } else {
                        me.selection = activeFlight.id;
                    }
                } else {
                    helpers.showNotification(me.container, me.dict['flight-terminated']);
                }
                delete me.initialSelection;
            } else {
                me.initialSelection = selection;
            }
        }

        return me;
    }

    /**
     * Returns the current clock mode.
     * @returns {string} Current clock mode: 'realtime' or 'playback'
     */
    getClockMode() {
        return this.clockMode;
    }

    /**
     * Sets the clock mode.
     * @param {string} mode - Clock mode: 'realtime' or 'playback'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setClockMode(mode) {
        const me = this;

        if (me.initialized) {
            me._setClockMode(mode);
        }
        return me;
    }

    /**
     * Returns the current eco mode.
     * @returns {string} Current eco mode: 'normal' or 'eco'
     */
    getEcoMode() {
        return this.ecoMode;
    }

    /**
     * Sets the eco mode.
     * @param {string} mode - Eco mode: 'normal' or 'eco'
     * @returns {Map} Returns itself to allow for method chaining
     */
    setEcoMode(mode) {
        const me = this;

        me._setEcoMode(mode);
        return me;
    }

    /**
     * Adds a layer to the map.
     * @param {Object | CustomLayerInterface | GeoJsonLayerInterface | ThreeLayerInterface | Tile3DLayerInterface} layer
     *     - The layer to add, conforming to either the Mapbox Style Specification's
     *     layer definition, the CustomLayerInterface specification, the
     *     GeoJsonLayerInterface specification, the ThreeLayerInterface specification
     *     or the Tile3DLayerInterface specification
     * @param {string} beforeId - The ID of an existing layer to insert the new
     *     layer before
     * @returns {Map} Returns itself to allow for method chaining
     */
    addLayer(layer, beforeId) {
        const me = this;

        if (layer.type === 'three') {
            new ThreeLayer(layer).onAdd(me, beforeId);
        } else if (layer.type === 'geojson') {
            new GeoJsonLayer(layer).onAdd(me, beforeId);
        } else if (layer.type === 'tile-3d') {
            new Tile3DLayer(layer).onAdd(me, beforeId);
        } else {
            me.map.addLayer(layer, beforeId || 'poi');
        }
        return me;
    }

    /**
     * Removes the layer with the given ID from the map.
     * @param {string} id - ID of the layer to remove
     * @returns {Map} Returns itself to allow for method chaining
     */
    removeLayer(id) {
        const me = this;

        me.map.removeLayer(id);
        return me;
    }

    /**
     * Sets the visibility of the layer.
     * @param {string} layerId - The ID of the layer to set the visibility in.
     * @param {string} visibility - Whether this layer is displayed.
     *     'visible' and 'none' are supported.
     * @returns {Map} Returns itself to allow for method chaining
     */
    setLayerVisibility(layerId, visibility) {
        const me = this;

        me.map.setLayoutProperty(layerId, 'visibility', visibility);
        return me;
    }

    /**
     * Returns a MercatorCoordinate object that represents the position of Tokyo
     * Station as the origin of the mercator coordinates.
     * @returns {MercatorCoordinate} The origin of the mercator coordinates
     */
    getModelOrigin() {
        return modelOrigin;
    }

    /**
     * Returns the scale to transform into MercatorCoordinate from coordinates in
     * real world units using meters. This provides the distance of 1 meter in
     * MercatorCoordinate units at Tokyo Station.
     * @returns {number} The scale to transform into MercatorCoordinate from
     *     coordinates in real world units using meters
     */
    getModelScale() {
        return modelScale;
    }

    /**
     * Projects a LngLat to a MercatorCoordinate, and returns the translated
     * mercator coordinates with Tokyo Station as the origin.
     * @param {LngLatLike} lnglat - The location to project
     * @param {number} altitude - The altitude in meters of the position
     * @returns {Object} The translated mercator coordinates with Tokyo Station as
     *     the origin
     */
    getModelPosition(lnglat, altitude) {
        const coord = MercatorCoordinate.fromLngLat(lnglat, altitude);

        return {
            x: coord.x - modelOrigin.x,
            y: -(coord.y - modelOrigin.y),
            z: coord.z - modelOrigin.z
        };
    }

    /**
     * Checks if the background color of the map is dark.
     * @returns {boolean} True if the background color of the map is dark
     */
    hasDarkBackground() {
        return helpersMapbox.hasDarkBackground(this.map);
    }

    initData(data) {
        const me = this,
            featureLookup = me.featureLookup = new Map(),
            stationGroupLookup = me.stationGroupLookup = new Map();

        me.dict = data.dict;
        me.featureCollection = data.featureCollection;

        me.stations = new Dataset(Station);
        me.railDirections = new Dataset(RailDirection, data.railDirectionData);
        me.railways = new Dataset(Railway, data.railwayData, {
            stations: me.stations,
            railDirections: me.railDirections
        });
        me.pois = new Dataset(POI, data.poiData);
        me.stations.load(data.stationData, {
            stations: me.stations,
            railways: me.railways,
            railDirections: me.railDirections,
            pois: me.pois
        });

        // Build feature lookup dictionary and update feature properties
        featureEach(me.featureCollection, feature => {
            const properties = feature.properties,
                {group, altitude} = properties;

            if (properties.type === 1) {
                // stations
                featureLookup.set(`${group}.${properties.zoom}`, feature);
                if (!stationGroupLookup.has(group)) {
                    stationGroupLookup.set(group, {
                        id: group,
                        type: 'station',
                        stations: properties.ids.map(id => me.stations.get(id)),
                        layer: altitude === 0 ? 'ground' : 'underground'
                    });
                }
            } else if (!(altitude <= 0)) {
                // airways and railways (no railway sections)
                featureLookup.set(properties.id, feature);
                helpersGeojson.updateDistances(feature);
            }
        });

        for (const station of me.stations.getAll()) {
            if (station.alternate) {
                for (const layer of ['og', 'ug']) {
                    const key = station.group.replace(/.g$/, layer),
                        stationGroup = stationGroupLookup.get(key);

                    if (stationGroup) {
                        if (!stationGroup.hidden) {
                            stationGroup.hidden = [];
                        }
                        stationGroup.hidden.push(station);
                    }
                }
            }
        }

        me.trainTypes = new Dataset(TrainType, data.trainTypeData);
        me.trainVehicleTypes = new Dataset(TrainVehicleType, data.trainVehicleData);

        me.lastTimetableRefresh = me.clock.getTime('03:00');
        me.dataReferences = {
            railways: me.railways,
            stations: me.stations,
            railDirections: me.railDirections,
            trainTypes: me.trainTypes,
            trainVehicleTypes: me.trainVehicleTypes
        };
        me.timetables = new TrainTimetables(data.timetableData, me.dataReferences);

        me.operators = new Dataset(Operator, data.operatorData);
        me.airports = new Dataset(Airport, data.airportData);
        me.flightStatuses = new Dataset(FlightStatus, data.flightStatusData);

        // 公共交通機能を無効化
        // me.activeTrainLookup = new Map();
        // me.standbyTrainLookup = new Map();
        // me.realtimeTrains = new Set();
        // me.activeFlightLookup = new Map();
        // me.flightLookup = new Map();

        me.gtfs = new Map();

        // Safie カメラデバイスの初期化と動的データ取得（LocationPanel統合版）
        me.activeSafieDevices = [];
        me.safieUpdateIntervalId = null;
        
        // SafieDeviceコンストラクタをLocationPanelで使用できるよう公開
        me.getConstructor = (name) => {
            const constructors = {
                'SafieDevice': SafieDevice
            };
            return constructors[name];
        };
        
        // Safieデバイス更新コールバック関数
        const updateSafieDevices = (devicesData) => {
            me.activeSafieDevices = [];
            
            // 取得したデバイスデータをSafieDeviceインスタンスに変換して保存
            for (const deviceData of devicesData) {
                const device = new SafieDevice(deviceData);
                me.activeSafieDevices.push(device);
            }
            
            console.log(`Safieデバイス更新完了: ${devicesData.length}台`);
             
            // 更新後の状態に合わせてSafieマーカーを再同期
            if (me.locationPanel) {
                console.log('Safieマーカー表示状態を再同期します');
                me.locationPanel._syncLocationMarkers(me, 'safie-cameras');
            }
            me.refreshSearchTargets();
        };
        
        // 定期更新を開始（LocationPanelで利用するため）
        me.safieUpdateIntervalId = startSafieDeviceUpdates(
            updateSafieDevices,
            configs.safieUpdateInterval
        );

        // Ichimill カメラデバイスの初期化と動的データ取得
        me.activeIchimillDevices = [];
        me.ichimillUpdateIntervalId = null;
        
        // getConstructorにIchimillDevice追加
        const originalGetConstructor = me.getConstructor;
        me.getConstructor = (name) => {
            if (name === 'IchimillDevice') return IchimillDevice;
            return originalGetConstructor(name);
        };
        
        // Ichimillデバイス更新コールバック関数
        const updateIchimillDevices = (devicesData) => {
            me.activeIchimillDevices = [];
            
            // 取得したデバイスデータをIchimillDeviceインスタンスに変換して保存
            for (const deviceData of devicesData) {
                const device = new IchimillDevice(deviceData);
                me.activeIchimillDevices.push(device);
            }
            
            console.log(`Ichimillデバイス更新完了: ${devicesData.length}台`);
            
            // 更新後の状態に合わせてIchimillマーカーを再同期
            if (me.locationPanel) {
                console.log('Ichimillマーカー表示状態を再同期します');
                me.locationPanel._syncLocationMarkers(me, 'ichimill-cameras');
            }
        };
        
        // 定期更新を開始
        me.ichimillUpdateIntervalId = startIchimillDeviceUpdates(
            updateIchimillDevices,
            configs.safieUpdateInterval
        );

        // 工事情報の初期化と動的データ取得
        me.activeKoujiLocations = [];
        me.koujiUpdateIntervalId = null;

        // getConstructorにKoujiLocation追加
        const originalGetConstructorForKouji = me.getConstructor;
        me.getConstructor = (name) => {
            if (name === 'KoujiLocation') return KoujiLocation;
            return originalGetConstructorForKouji(name);
        };

        // 工事情報更新コールバック関数
        const updateKoujiLocations = (locationsData) => {
            me.activeKoujiLocations = [];

            for (const locationData of locationsData) {
                const location = new KoujiLocation(locationData);
                me.activeKoujiLocations.push(location);
            }

            console.log(`工事情報更新完了: ${locationsData.length}件`);

            if (me.locationPanel) {
                console.log('工事情報マーカー表示状態を再同期します');
                me.locationPanel._syncLocationMarkers(me, 'kouji-under-construction');
                me.locationPanel._syncLocationMarkers(me, 'kouji-complete');
            }
            me.refreshSearchTargets();
        };

        // 定期更新を開始
        me.koujiUpdateIntervalId = startKoujiLocationUpdates(
            updateKoujiLocations,
            configs.safieUpdateInterval
        );
    }

    initialize() {
        const me = this,
            {lang, dict, clock, map, container, featureCollection} = me,
            initialZoom = map.getZoom(),
            layerZoom = me.layerZoom = getLayerZoom(initialZoom);

        if (map.loaded()) {
            hideLoader(container);
        } else {
            map.once('load', () => {
                hideLoader(container);
            });
        }

        // Deprecated
        // me.objectUnit = Math.max(getObjectScale(initialZoom) * .19, .02);

        me.trafficLayer = new TrafficLayer({id: 'traffic'});

        // To move to the style file in v4.0
        map.setLight({
            intensity: 0.35,
            anchor: 'map'
        });

        // To move to the style file in v4.0
        map.addLayer({
            id: 'sky',
            type: 'sky',
            paint: {
                'sky-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    0,
                    0,
                    5,
                    0.3,
                    8,
                    1
                ],
                'sky-type': 'atmosphere',
                'sky-atmosphere-sun-intensity': 20
            }
        }, 'background');

        // To move to the style file in v4.0
        map.addLayer({
            id: 'background-underground',
            type: 'background',
            paint: {
                'background-color': 'rgba(16,16,16,1)',
                'background-opacity': 0
            },
            metadata: {
                'mt3d:opacity-effect': true,
                'mt3d:opacity': 0,
                'mt3d:opacity-underground': 1
            }
        }, 'natural_earth');

        // To move to the style file in v4.0
        map.addLayer({
            id: 'building-underground-underground',
            type: 'fill',
            source: 'mapbox',
            'source-layer': 'building',
            minzoom: 14.5,
            filter: [
                'all',
                [
                    'match',
                    ['get', 'type'],
                    ['underground_mall', 'subway'],
                    true,
                    false
                ],
                ['==', ['get', 'underground'], 'true'],
                ['==', ['geometry-type'], 'Polygon']
            ],
            layout: {},
            paint: {
                'fill-outline-color': 'hsl(35, 8%, 80%)',
                'fill-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    14.5,
                    0,
                    15,
                    0
                ],
                'fill-color': 'hsla(268, 67%, 67%, 0.5)'
            },
            metadata: {
                'mt3d:opacity-effect': true,
                'mt3d:opacity': 0,
                'mt3d:opacity-underground': 1,
                'mt3d:opacity-underground-route': 0.1
            }
        }, 'building');

        map.setLayoutProperty('poi', 'text-field', [
            'coalesce',
            ['get', `name_${lang}`],
            ['get', 'name_en'],
            ['get', 'name']
        ]);

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const commonProps = {
                type: 'geojson',
                lineWidthUnits: 'pixels',
                lineWidthScale:
                    zoom === 13 ? helpers.clamp(Math.pow(2, initialZoom - 12), .125, 1) :
                    zoom === 18 ? helpers.clamp(Math.pow(2, initialZoom - 19), 1, 8) : 1,
                parameters: {depthTest: false},
                minzoom: zoom <= 13 ? 0 : zoom,
                maxzoom: zoom >= 18 ? 24 : zoom + 1
            };

            for (const key of ['marked', 'selected']) {
                me.addLayer(Object.assign({}, commonProps, {
                    id: `stations-${key}-${zoom}`,
                    getLineWidth: 12,
                    getLineColor: [255, 255, 255],
                    getFillColor: [255, 255, 255],
                    visible: false
                }), 'building-3d');
            }

            // 3D地下レイヤー（railways-ug, stations-ug）の非表示化
            // for (const key2 of ['ug', 'routeug', 'routeog']) {
            //     for (const key1 of ['railways', 'stations']) {
            for (const key2 of []) {
                for (const key1 of []) {
                    const type = key1 === 'railways' ? 0 : 1,
                        filter = p => p.zoom === zoom && p.type === type && p.altitude < 0;

                    me.addLayer(Object.assign({}, commonProps, {
                        id: `${key1}-${key2}-${zoom}`,
                        data: key2 === 'ug' ?
                            helpersGeojson.featureFilter(featureCollection, filter) :
                            helpersGeojson.emptyFeatureCollection(),
                        transitions: {opacity: configs.transitionDuration}
                    }, {
                        'railways': {
                            filled: false,
                            getLineWidth: d => d.properties.width,
                            getLineColor: d => helpers.colorToRGBArray(d.properties.color)
                        },
                        'stations': {
                            getLineWidth: 4,
                            getFillColor: [255, 255, 255, 179]
                        }
                    }[key1], {
                        'ug': {
                            opacity: .0625,
                            pickable: key1 === 'stations',
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 0.0625,
                                'mt3d:opacity-route': 0.005,
                                'mt3d:opacity-underground': 1,
                                'mt3d:opacity-underground-route': 0.005
                            }
                        },
                        'routeug': {
                            opacity: .0625,
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 0.125,
                                'mt3d:opacity-underground': 1
                            }
                        },
                        'routeog': {
                            metadata: {
                                'mt3d:opacity-effect': true,
                                'mt3d:opacity': 1,
                                'mt3d:opacity-underground': 0.25
                            }
                        }
                    }[key2]), 'building-3d');
                }
            }
        }

        // Workaround for deck.gl #3522
        map.__deck.props.getCursor = () => map.getCanvas().style.cursor;

        map.addSource('odpt', {
            type: 'geojson',
            data: helpersGeojson.featureFilter(featureCollection, p => p.altitude === 0)
        });

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            const interpolate = ['interpolate', ['exponential', 2], ['zoom']],
                width = ['get', 'width'],
                color = ['get', 'color'],
                lineWidth =
                    zoom === 13 ? [...interpolate, 9, ['/', width, 8], 12, width] :
                    zoom === 18 ? [...interpolate, 19, width, 22, ['*', width, 8]] :
                    width;

            // 路線図レイヤーの非表示化
            // for (const key of ['railways', 'stations', 'stations-outline']) {
            for (const key of []) {
                map.addLayer({
                    id: `${key}-og-${zoom}`,
                    type: key === 'stations' ? 'fill' : 'line',
                    source: 'odpt',
                    filter: [
                        'all',
                        ['==', ['get', 'zoom'], zoom],
                        ['==', ['get', 'type'], key === 'railways' ? 0 : 1]
                    ],
                    layout: {
                        visibility: zoom === layerZoom ? 'visible' : 'none'
                    },
                    paint: {
                        'railways': {
                            'line-color': color,
                            'line-width': lineWidth
                        },
                        'stations': {
                            'fill-color': color,
                            'fill-opacity': .7
                        },
                        'stations-outline': {
                            'line-color': ['get', 'outlineColor'],
                            'line-width': lineWidth
                        }
                    }[key],
                    metadata: {
                        'mt3d:opacity-effect': true,
                        'mt3d:opacity': 1,
                        'mt3d:opacity-route': 0.1,
                        'mt3d:opacity-underground': 0.25,
                        'mt3d:opacity-underground-route': 0.1
                    }
                }, 'building-3d');
            }
        }

        me.addLayer(me.trafficLayer, 'building-3d');

        /* For development
        me.addLayer({
            id: `airway-og-`,
            type: 'geojson',
            data: helpersGeojson.featureFilter(featureCollection, p =>
                p.type === 0 && p.altitude > 0
            ),
            filled: false,
            getLineWidth: d => d.properties.width,
            getLineColor: d => helpers.colorToRGBArray(d.properties.color),
            lineWidthUnits: 'pixels',
            lineWidthScale: 1,
            opacity: .0625,
            transitions: {opacity: configs.transitionDuration},
            parameters: {depthTest: false}
        });
        */

        me.styleColors = helpersMapbox.getStyleColors(map, 'mt3d:color-effect');
        me.styleOpacities = helpersMapbox.getStyleOpacities(map, 'mt3d:opacity-effect');

        const datalist = helpers.createElement('datalist', {id: 'stations'}, document.body);
        me.searchTargetDatalist = helpers.createElement('datalist', {id: 'search-targets'}, document.body);
        const stationTitleLookup = me.stationTitleLookup = new Map();
        me.searchTargetLookup = new Map();

        for (const l of [lang, 'en']) {
            for (const railway of me.railways.getAll()) {
                for (const station of railway.stations) {
                    const {title, utitle} = station,
                        stationTitle = (utitle && utitle[l]) || helpers.normalize(title[l] || title.en),
                        key = stationTitle.toUpperCase();

                    if (!stationTitleLookup.has(key)) {
                        helpers.createElement('option', {value: stationTitle}, datalist);
                        stationTitleLookup.set(key, station);
                    }
                }
            }
        }
        me.refreshSearchTargets();

        if (me.searchControl) {
            const control = new SearchControl({
                title: me.dict['search'],
                placeholder: lang === 'ja' ? '工事名・カメラ名' : 'Construction or camera name',
                list: 'search-targets',
                eventHandler: ({value}) => me.selectSearchTarget(value)
            });

            map.addControl(control);
        }

        if (me.navigationControl) {
            const control = me.navControl = new NavigationControl();

            control._setButtonTitle = function(button) {
                const {_zoomInButton, _zoomOutButton, _compass} = this,
                    title = button === _zoomInButton ? dict['zoom-in'] :
                    button === _zoomOutButton ? dict['zoom-out'] :
                    button === _compass ? dict['compass'] : '';

                button.title = title;
                button.setAttribute('aria-label', title);
            };
            map.addControl(control);
        }

        if (me.fullscreenControl) {
            const control = new FullscreenControl({container});

            control._updateTitle = function() {
                const button = this._fullscreenButton,
                    title = dict[this._isFullscreen() ? 'exit-fullscreen' : 'enter-fullscreen'];

                button.title = title;
                button.setAttribute('aria-label', title);
            };
            map.addControl(control);
        }

        map.addControl(new ScaleControl({
            unit: 'metric'
        }), 'bottom-left');

        if (me.modeControl) {
            const control = new MapboxGLButtonControl([{
                className: 'mapboxgl-ctrl-underground',
                title: dict['enter-underground'],
                eventHandler() {
                    me._setViewMode(me.viewMode === 'ground' ? 'underground' : 'ground');
                }
            }, {
                className: 'mapboxgl-ctrl-playback',
                title: dict['enter-playback'],
                eventHandler() {
                    me._setClockMode(me.clockMode === 'realtime' ? 'playback' : 'realtime');
                }
            }, {
                className: 'mapboxgl-ctrl-eco',
                title: dict['enter-eco'],
                eventHandler() {
                    me._setEcoMode(me.ecoMode === 'eco' ? 'normal' : 'eco');
                }
            }]);

            map.addControl(control);
        }

        me.layerPanel = new LayerPanel({layers: me.plugins});
        me.locationPanel = new LocationPanel({
            locationItems: [
                {
                    id: 'safie-cameras',
                    name: dict['safie-cameras'] || 'Safieカメラ',
                    color: '#ff6b6b',
                    enabled: true
                },
                {
                    id: 'ichimill-cameras',
                    name: dict['ichimill-cameras'] || '建設機械(開発中)',
                    color: '#ff9800',
                    enabled: true
                },
                {
                    id: 'kouji-under-construction',
                    name: dict['kouji-under-construction'] || '工事情報(工事中)',
                    color: '#ff3b30',
                    enabled: true
                },
                {
                    id: 'kouji-complete',
                    name: dict['kouji-complete'] || '工事情報(竣工)',
                    color: '#1e90ff',
                    enabled: true
                }
            ]
        });
        me.locationPanel._bindPopupAutoClose(me);
        me.trackingModePanel = new TrackingModePanel();
        me.aboutPanel = new AboutPanel();
        me.tsunamiPanel = new TsunamiPanel({
            onSelectTsunami: (eventId, stationCode = '') => {
                me.selectTsunami(eventId, stationCode);
                me.moveToTsunamiSelection(eventId, stationCode);
            },
            onClose: () => {
                me._tsunamiMarkersVisible = false;
                me.clearTsunamiMarkers();
                me.updateDisasterNotificationButtons();
            }
        });
        me.earthquakePanel = new EarthquakePanel({
            onSelectEarthquake: eventId => {
                me.selectEarthquake(eventId);
                me.moveToEarthquakeSelection(eventId);
            },
            onClose: () => {
                me._earthquakeMarkersVisible = false;
                me.clearEarthquakeMarkers();
                me.updateDisasterNotificationButtons();
            }
        });
        me.typhoonPanel = new TyphoonPanel({
            onSelectTyphoon: eventId => {
                me.selectTyphoon(eventId);
                me.moveToTyphoonSelection(eventId);
            },
            onClose: () => {
                me._typhoonLayersVisible = false;
                me.renderTyphoonLayers();
                me.updateDisasterNotificationButtons();
            }
        });
        me.initializeTsunamiFeature();
        me.initializeEarthquakeFeature();
        me.initializeTyphoonFeature();

        if (me.configControl) {
            map.addControl(new MapboxGLButtonControl([{
                className: 'mapboxgl-ctrl-layers',
                title: dict['select-layers'],
                eventHandler() {
                    me.layerPanel.addTo(me);
                }
            // 追跡モードとアバウトをコメントアウト
            // }, {
            //     className: 'mapboxgl-ctrl-tracking-mode',
            //     title: dict['select-tracking-mode'],
            //     eventHandler() {
            //         me.trackingModePanel.addTo(me);
            //     }
            // }, {
            //     className: 'mapboxgl-ctrl-about',
            //     title: dict['about'],
            //     eventHandler() {
            //         me.aboutPanel.addTo(me);
            //     }
            }]));
        }

        // 位置情報コントロールを追加
        if (me.configControl) {
            map.addControl(new MapboxGLButtonControl([{
                className: 'mapboxgl-ctrl-locations',
                title: dict['select-locations'],
                eventHandler() {
                    me.locationPanel.addTo(me);
                }
            }]));
        }

        if (me.clockControl) {
            const control = me.clockCtrl = new ClockControl({lang, dict, clock});

            control.on('change', me.onClockChange.bind(me));
            map.addControl(control);
        }

        // 駅ポップアップ機能を無効化
        // map.on('mousemove', e => {
        //     me.markObject(me.pickObject(e.point));
        // });

        // map.on('mouseout', () => {
        //     me.markObject();
        // });

        // map.on('click', e => {
        //     const object = me.pickObject(e.point);

        //     me.markObject(object);
        //     me.trackObject(object);

        //     // For development
        //     console.log(e.lngLat);
        // });

        map.on('zoom', e => {
            if (!e.tracking) {
                me.updateBaseZoom();
            }

            const zoom = map.getZoom(),
                prevLayerZoom = me.layerZoom,
                layerZoom = me.layerZoom = getLayerZoom(zoom);

            if (zoom < 13) {
                const lineWidthScale = helpers.clamp(Math.pow(2, zoom - 12), .125, 1);

                // 路線関連の線幅スケール調整の無効化
                // for (const id of ['stations-marked-13', 'stations-selected-13', 'railways-ug-13', 'stations-ug-13', 'railways-routeug-13', 'stations-routeug-13', 'railways-routeog-13', 'stations-routeog-13']) {
                for (const id of []) {
                    helpersMapbox.setLayerProps(map, id, {lineWidthScale});
                }
            } else if (zoom > 19) {
                const lineWidthScale = helpers.clamp(Math.pow(2, zoom - 19), 1, 8);

                // 路線関連の線幅スケール調整の無効化
                // for (const id of ['stations-marked-18', 'stations-selected-18', 'railways-ug-18', 'stations-ug-18', 'railways-routeug-18', 'stations-routeug-18', 'railways-routeog-18', 'stations-routeog-18']) {
                for (const id of []) {
                    helpersMapbox.setLayerProps(map, id, {lineWidthScale});
                }
            }

            // Deprecated
            // me.objectUnit = Math.max(getObjectScale(zoom) * .19, .02);

            if (prevLayerZoom !== layerZoom) {
                // 路線図レイヤーの非表示化
                // for (const key of ['railways', 'stations', 'stations-outline']) {
                for (const key of []) {
                    me.setLayerVisibility(`${key}-og-${prevLayerZoom}`, 'none');
                    me.setLayerVisibility(`${key}-og-${layerZoom}`, 'visible');
                }

                for (const {id} of me.gtfs.values()) {
                    for (const key of ['busstops', 'busstops-outline']) {
                        if (prevLayerZoom >= 14) {
                            me.setLayerVisibility(`${key}-${id}-og-${prevLayerZoom}`, 'none');
                        }
                        if (layerZoom >= 14) {
                            me.setLayerVisibility(`${key}-${id}-og-${layerZoom}`, 'visible');
                        }
                    }
                }

                if (e.tracking) {
                    me.prevLayerZoom = prevLayerZoom;
                } else {
                    delete me.prevLayerZoom;
                }

                // If the layer is switched, all object positions need to be recalculated
                for (const train of me.activeTrainLookup.values()) {
                    me.updateTrainProps(train);
                    me.updateTrainShape(train);
                }
                for (const flight of me.activeFlightLookup.values()) {
                    me.updateFlightShape(flight);
                }
            }
        });

        map.on('render', () => {
            const markedObject = me.markedObject;

            if (markedObject) {
                // Popup for a 3D object needs to be updated every time
                // because the adjustment for altitude is required
                me.updatePopup();
            }
        });

        for (const plugin of me.plugins.slice().reverse()) {
            plugin.addTo(me);
        }

        animation.init();

        animation.start({
            callback: () => {
                const clock = me.clock,
                    now = clock.getTime(),
                    {minDelay, realtimeCheckInterval} = configs;

                if (now - me.lastTimetableRefresh >= 86400000) {
                    // リアルタイム公共交通データ更新の停止
                    // me.refreshTrainTimetableData();
                    // me.refreshBusData();
                    me.lastTimetableRefresh = clock.getTime('03:00');
                }

                // Remove all trains if the page has been invisible for certain amount of time
                if (Date.now() - me.lastFrameRefresh >= configs.refreshTimeout) {
                    me.stopAll();
                }
                me.lastFrameRefresh = Date.now();

                me.updateVisibleArea();

                if (Math.floor((now - minDelay) / realtimeCheckInterval) !== Math.floor(me.lastTrainRefresh / realtimeCheckInterval)) {
                    helpersMapbox.setStyleColors(map, me.styleColors, me.getLightColor());
                    helpersMapbox.setSunlight(map, now);
                    if (me.searchMode === 'none') {
                        if (me.clockMode === 'realtime') {
                            // リアルタイム公共交通データ更新の停止
                            // me.refreshRealtimeTrainData();
                            // me.refreshRealtimeFlightData();
                            // me.refreshRealtimeBusData();
                        } else {
                            // リアルタイム公共交通データ更新の停止
                            // me.refreshTrains();
                            // me.refreshFlights();
                        }
                        if (isStation(me.trackedObject)) {
                            me.detailPanel.updateContent();
                        }
                    }
                    me.lastTrainRefresh = now - minDelay;
                }

                if (!isVehicle(me.trackedObject) && ((me.ecoMode === 'normal' && map._loaded) || Date.now() - me.lastRepaint >= 1000 / me.ecoFrameRate)) {
                    if (me.trackedObject) {
                        me.refreshStationOutline();
                    }
                    map.triggerRepaint();
                    me.lastRepaint = Date.now();
                }
            }
        });

        me.initialized = true;
        me.fire({type: 'initialized'});
        me.maybeApplyInitialDisasterDisplay();
    }

    ensureDisasterNotificationContainer() {
        const me = this;

        if (me._disasterNotificationContainer) {
            return me._disasterNotificationContainer;
        }

        me._disasterNotificationContainer = helpers.createElement('div', {
            className: 'disaster-notification-container'
        }, me.container);

        return me._disasterNotificationContainer;
    }

    initializeTsunamiFeature() {
        const me = this;

        if (!configs.apiUrl.tsunami) {
            return;
        }

        me._tsunamiMarkers = [];
        me._tsunamiMarkersVisible = false;
        me._tsunamiHasUnread = false;
        me._lastReadTsunamiEventId = '';
        me.tsunamiEvents = [];
        me.selectedTsunamiEventId = '';
        me.selectedTsunamiStationCode = '';

        const notificationContainer = me.ensureDisasterNotificationContainer();

        const button = me._tsunamiButton = helpers.createElement('button', {
            className: 'tsunami-button tsunami-hidden',
            type: 'button'
        }, notificationContainer);
        const text = helpers.createElement('span', {
            className: 'tsunami-button-text'
        }, button);

        me._tsunamiButtonLabel = helpers.createElement('span', {
            className: 'tsunami-button-label',
            textContent: '津波情報'
        }, text);
        me._tsunamiButtonSummary = helpers.createElement('span', {
            className: 'tsunami-button-summary'
        }, text);
        me._tsunamiButtonBadge = helpers.createElement('span', {
            className: 'tsunami-button-badge tsunami-hidden'
        }, button);

        button.addEventListener('click', () => {
            me.openTsunamiDetail();
        });

        me.tsunamiUpdateIntervalId = startTsunamiUpdates(
            me.handleTsunamiUpdate.bind(me),
            TSUNAMI_REFRESH_INTERVAL
        );
    }

    getLatestTsunami() {
        return (this.tsunamiEvents || [])[0] || null;
    }

    getSelectedTsunami() {
        const me = this,
            tsunamis = me.tsunamiEvents || [];

        if (isDisasterSelectAll(me.selectedTsunamiEventId)) {
            return null;
        }
        return tsunamis.find(tsunami => tsunami.eventid === me.selectedTsunamiEventId) || tsunamis[0] || null;
    }

    getDisasterDetailPanels() {
        return [
            {type: 'tsunami', panel: this.tsunamiPanel},
            {type: 'earthquake', panel: this.earthquakePanel},
            {type: 'typhoon', panel: this.typhoonPanel}
        ].filter(({panel}) => panel);
    }

    isDisasterDetailPanelOpen(panel) {
        return !!(panel && typeof panel.isOpen === 'function' && panel.isOpen());
    }

    isDisasterDetailPanelClosing(panel) {
        return !!(panel && typeof panel.isClosing === 'function' && panel.isClosing());
    }

    hasOpenDisasterDetail(excludeType = '') {
        return this.getDisasterDetailPanels().some(({type, panel}) =>
            type !== excludeType && this.isDisasterDetailPanelOpen(panel) && !this.isDisasterDetailPanelClosing(panel)
        );
    }

    closeOtherDisasterDetails(activeType) {
        this.getDisasterDetailPanels().forEach(({type, panel}) => {
            if (
                type !== activeType &&
                this.isDisasterDetailPanelOpen(panel) &&
                !this.isDisasterDetailPanelClosing(panel)
            ) {
                panel.remove();
            }
        });
    }

    updateDisasterNotificationButtons() {
        if (typeof this.updateTsunamiButton === 'function') {
            this.updateTsunamiButton();
        }
        if (typeof this.updateEarthquakeButton === 'function') {
            this.updateEarthquakeButton();
        }
        if (typeof this.updateTyphoonButton === 'function') {
            this.updateTyphoonButton();
        }
    }

    markInitialDisasterFetchComplete(type) {
        if (!this._initialDisasterFetchState || !(type in this._initialDisasterFetchState)) {
            return;
        }

        this._initialDisasterFetchState[type] = true;
        this.maybeApplyInitialDisasterDisplay();
    }

    areInitialDisasterFetchesComplete() {
        const state = this._initialDisasterFetchState;

        return Boolean(state && state.earthquake && state.tsunami && state.typhoon);
    }

    getInitialDisasterDisplayTarget() {
        const latestEarthquake = this.getLatestEarthquake(),
            latestTsunami = this.getLatestTsunami(),
            latestTyphoon = this.getLatestTyphoon();

        if (latestEarthquake && this.hasRecentEarthquakes(this.earthquakeEvents || [])) {
            return {
                type: 'earthquake',
                eventId: latestEarthquake.eventid
            };
        }
        if (latestTsunami && this.hasRecentTsunamis(this.tsunamiEvents || [])) {
            return {
                type: 'tsunami',
                eventId: latestTsunami.eventid
            };
        }
        if (latestTyphoon) {
            return {
                type: 'typhoon',
                eventId: latestTyphoon.eventid
            };
        }
        return null;
    }

    maybeApplyInitialDisasterDisplay() {
        if (this._initialDisasterDisplayResolved || !this.initialized || !this.areInitialDisasterFetchesComplete()) {
            return;
        }

        const target = this.getInitialDisasterDisplayTarget();

        this._initialDisasterDisplayResolved = true;

        if (!target) {
            this.flyToDisasterOverview();
            return;
        }

        if (target.type === 'earthquake') {
            this.openEarthquakeDetail(target.eventId);
        } else if (target.type === 'tsunami') {
            this.openTsunamiDetail(target.eventId);
        } else if (target.type === 'typhoon') {
            this.openTyphoonDetail(target.eventId);
        }
    }

    flyToDisasterOverview() {
        this.flyTo({
            center: DISASTER_OVERVIEW_CENTER,
            zoom: DISASTER_OVERVIEW_ZOOM
        });
    }

    getCameraStateSnapshot() {
        const center = this.getCenter();

        return {
            center: [center.lng, center.lat],
            zoom: this.getZoom(),
            bearing: this.getBearing(),
            pitch: this.getPitch(),
            viewMode: this.getViewMode()
        };
    }

    rememberCameraBeforeTyphoonDisplay() {
        if (this.typhoonCameraRestoreState || this.isDisasterDetailPanelOpen(this.typhoonPanel)) {
            return;
        }

        this.typhoonCameraRestoreState = this.getCameraStateSnapshot();
    }

    restoreCameraAfterTyphoonDisplay({instant = true, skipTrackObject = false} = {}) {
        const me = this,
            state = me.typhoonCameraRestoreState;

        if (!state) {
            return false;
        }

        me.typhoonCameraRestoreState = null;

        if (!skipTrackObject) {
            me.trackObject();
        }

        me._setViewMode(state.viewMode);
        me.map[instant ? 'jumpTo' : 'flyTo']({
            center: state.center,
            zoom: state.zoom,
            bearing: state.bearing,
            pitch: state.pitch
        });

        return true;
    }

    getAllTsunamiStations() {
        return mergeTsunamiStationsByLatestObservation(
            (this.tsunamiEvents || []).flatMap(tsunami => tsunami.mapStations || [])
        );
    }

    getAllTsunamiCauseEarthquakes() {
        const earthquakes = (this.tsunamiEvents || [])
            .map(tsunami => tsunami.causeEarthquake)
            .filter(earthquake => earthquake && earthquake.hasHypocenterCoordinate);
        const uniqueEarthquakes = new Map();

        earthquakes.forEach(earthquake => {
            const key = earthquake.eventid ||
                `${earthquake.hypocenterLongitude},${earthquake.hypocenterLatitude}`;

            if (!uniqueEarthquakes.has(key)) {
                uniqueEarthquakes.set(key, earthquake);
            }
        });
        return Array.from(uniqueEarthquakes.values());
    }

    getTsunamiSelectionCoordinates(tsunami, stationCode = this.selectedTsunamiStationCode) {
        const stations = tsunami ?
                (
                    stationCode ?
                        tsunami.mapStations.filter(item => item.stationcode === stationCode) :
                        tsunami.mapStations
                ) :
                [],
            causeEarthquake = tsunami?.causeEarthquake,
            coordinates = stations.map(station => [station.longitude, station.latitude]);

        if (causeEarthquake?.hasHypocenterCoordinate) {
            coordinates.push([
                causeEarthquake.hypocenterLongitude,
                causeEarthquake.hypocenterLatitude
            ]);
        }

        return coordinates;
    }

    getEarthquakeWeightedCenter(earthquake) {
        const municipalities = earthquake?.mapMunicipalities || [];

        if (!municipalities.length) {
            return null;
        }

        const weighted = municipalities.reduce((result, municipality) => {
            const weight = Math.max(getEarthquakeIntensityRank(municipality.rawIntensity), 1);

            return {
                longitude: result.longitude + municipality.longitude * weight,
                latitude: result.latitude + municipality.latitude * weight,
                weight: result.weight + weight
            };
        }, {
            longitude: 0,
            latitude: 0,
            weight: 0
        });

        if (!weighted.weight) {
            return null;
        }

        return [weighted.longitude / weighted.weight, weighted.latitude / weighted.weight];
    }

    moveToTsunamiSelection(eventId = this.selectedTsunamiEventId, stationCode = this.selectedTsunamiStationCode) {
        if (isDisasterSelectAll(eventId)) {
            this.flyToDisasterOverview();
            return;
        }

        const tsunami = (this.tsunamiEvents || []).find(item => item.eventid === eventId) || this.getSelectedTsunami(),
            coordinates = this.getTsunamiSelectionCoordinates(tsunami, stationCode),
            station = tsunami ?
                (
                    (stationCode && tsunami.mapStations.find(item => item.stationcode === stationCode)) ||
                    tsunami.mapStations[0] ||
                    tsunami.stations.find(item => item.hasCoordinate)
                ) :
                null;

        if (coordinates.length > 1) {
            this.map.fitBounds(helpersMapbox.getBounds(coordinates), {
                padding: {top: 40, bottom: 40, left: 40, right: 40},
                maxZoom: 7.5
            });
            return;
        }
        if (coordinates.length === 1) {
            this.flyTo({
                center: coordinates[0],
                zoom: Math.max(this.getZoom(), 6.5)
            });
            return;
        }
        if (!station) {
            this.flyToDisasterOverview();
            return;
        }

        this.flyTo({
            center: [station.longitude, station.latitude],
            zoom: Math.max(this.getZoom(), 6.5)
        });
    }

    moveToTyphoonSelection(eventId = this.selectedTyphoonEventId) {
        if (isDisasterSelectAll(eventId)) {
            this.flyToDisasterOverview();
            return;
        }

        const typhoon = (this.typhoonEvents || []).find(item => item.eventid === eventId) || this.getSelectedTyphoon();

        if (!typhoon) {
            this.flyToDisasterOverview();
            return;
        }
        if (!eventId && !typhoon.isInitialDisplayTarget) {
            this.flyToDisasterOverview();
            return;
        }

        const featureCollection = typhoon.mapFeatureCollection,
            coordinates = featureCollection?.features?.reduce((result, feature) =>
                collectCoordinatesFromGeometry(feature.geometry, result), []) || [];

        if (coordinates.length > 1) {
            this.map.fitBounds(helpersMapbox.getBounds(coordinates), {
                padding: {top: 40, bottom: 40, left: 40, right: 40},
                maxZoom: 7.5
            });
            return;
        }

        if (typhoon.current?.coordinate) {
            this.flyTo({
                center: typhoon.current.coordinate,
                zoom: Math.max(this.getZoom(), 6)
            });
            return;
        }

        this.flyToDisasterOverview();
    }

    moveToEarthquakeSelection(eventId = this.selectedEarthquakeEventId) {
        if (isDisasterSelectAll(eventId)) {
            this.flyToDisasterOverview();
            return;
        }

        const earthquake = (this.earthquakeEvents || []).find(item => item.eventid === eventId) || this.getSelectedEarthquake(),
            center = this.getEarthquakeWeightedCenter(earthquake);

        if (!center) {
            this.flyToDisasterOverview();
            return;
        }

        this.flyTo({
            center,
            zoom: Math.max(this.getZoom(), 6.5)
        });
    }

    syncTsunamiPanel() {
        const me = this;

        me.tsunamiPanel
            .setTsunamis(
                me.tsunamiEvents || [],
                me.selectedTsunamiEventId || ''
            )
            .setSelectedStationCode(me.selectedTsunamiStationCode || '');
    }

    hasRecentTsunamis(tsunamis = this.tsunamiEvents || []) {
        const now = Date.now();

        return tsunamis.some(tsunami => {
            const timestamp = tsunami && tsunami.report_datetime ? new Date(tsunami.report_datetime).getTime() : NaN;

            return Number.isFinite(timestamp) && now >= timestamp && now - timestamp <= TSUNAMI_NEW_WINDOW;
        });
    }

    markTsunamiAsRead(eventId = null) {
        const latestTsunami = this.getLatestTsunami(),
            resolvedEventId = eventId || (latestTsunami && latestTsunami.eventid);

        if (!resolvedEventId) {
            return;
        }

        this._lastReadTsunamiEventId = resolvedEventId;
        this._tsunamiHasUnread = this.hasRecentTsunamis();
        this.updateTsunamiButton();
    }

    selectTsunami(eventId, stationCode = '') {
        const me = this;

        if (isDisasterSelectAll(eventId)) {
            me.selectedTsunamiEventId = DISASTER_SELECT_ALL;
            me.selectedTsunamiStationCode = '';
            me.syncTsunamiPanel();

            if (me._tsunamiMarkersVisible) {
                me.renderTsunamiMarkers();
            }
            return;
        }

        const tsunami = (me.tsunamiEvents || []).find(item => item.eventid === eventId);

        if (!tsunami) {
            return;
        }

        me.selectedTsunamiEventId = tsunami.eventid;
        if (stationCode && tsunami.stations.some(station => station.stationcode === stationCode)) {
            me.selectedTsunamiStationCode = stationCode;
        } else if (
            me.selectedTsunamiStationCode &&
            !tsunami.stations.some(station => station.stationcode === me.selectedTsunamiStationCode)
        ) {
            me.selectedTsunamiStationCode = '';
        }
        me.syncTsunamiPanel();

        if (me._tsunamiMarkersVisible) {
            me.renderTsunamiMarkers();
        }
    }

    setSelectedTsunamiStationCode(stationCode) {
        this.selectedTsunamiStationCode = stationCode || '';

        if (this.tsunamiPanel && this.tsunamiPanel.isOpen()) {
            this.tsunamiPanel.setSelectedStationCode(this.selectedTsunamiStationCode);
        }
    }

    handleTsunamiUpdate(tsunamis) {
        const me = this,
            nextTsunamis = Array.isArray(tsunamis) ? tsunamis : [],
            nextLatestTsunami = nextTsunamis[0] || null;

        if (!nextTsunamis.length) {
            me.tsunamiEvents = [];
            me.selectedTsunamiEventId = '';
            me.selectedTsunamiStationCode = '';
            me._tsunamiHasUnread = false;
            me.syncTsunamiPanel();
            me.updateTsunamiButton();
            me.clearTsunamiMarkers();
            me._tsunamiMarkersVisible = false;
            if (me.tsunamiPanel && me.tsunamiPanel.isOpen()) {
                me.tsunamiPanel.remove();
            }
            me.markInitialDisasterFetchComplete('tsunami');
            return;
        }

        me.tsunamiEvents = nextTsunamis;
        me._tsunamiHasUnread = me.hasRecentTsunamis(nextTsunamis);
        if (
            (!me.tsunamiPanel.isOpen() && !isDisasterSelectAll(me.selectedTsunamiEventId)) ||
            !nextTsunamis.some(tsunami => tsunami.eventid === me.selectedTsunamiEventId)
        ) {
            if (!isDisasterSelectAll(me.selectedTsunamiEventId)) {
                me.selectedTsunamiEventId = nextLatestTsunami.eventid;
            }
        }
        const selectedTsunami = me.getSelectedTsunami();
        if (
            me.selectedTsunamiStationCode &&
            selectedTsunami &&
            !selectedTsunami.stations.some(station => station.stationcode === me.selectedTsunamiStationCode)
        ) {
            me.selectedTsunamiStationCode = '';
        }

        me.syncTsunamiPanel();
        me.updateTsunamiButton();

        if (me.tsunamiPanel.isOpen()) {
            me.tsunamiPanel.updateContent();
        }
        if (me._tsunamiMarkersVisible) {
            me.renderTsunamiMarkers();
        }
        me.markInitialDisasterFetchComplete('tsunami');
    }

    updateTsunamiButton() {
        const me = this,
            button = me._tsunamiButton,
            label = me._tsunamiButtonLabel,
            summary = me._tsunamiButtonSummary,
            badge = me._tsunamiButtonBadge,
            latestTsunami = me.getLatestTsunami();

        if (!button || !label || !summary || !badge) {
            return;
        }

        if (!latestTsunami) {
            button.classList.add('tsunami-hidden');
            badge.classList.add('tsunami-hidden');
            summary.textContent = '';
            label.textContent = '津波情報';
            button.classList.remove('tsunami-button-new');
            return;
        }

        if (me.hasOpenDisasterDetail()) {
            button.classList.add('tsunami-hidden');
            badge.classList.add('tsunami-hidden');
            return;
        }

        label.textContent = me._tsunamiHasUnread ? '津波情報 NEW' : '津波情報';
        summary.textContent = buildTsunamiButtonSummary(latestTsunami);
        button.classList.remove('tsunami-hidden');
        button.classList.toggle('tsunami-button-new', me._tsunamiHasUnread);
        badge.classList.toggle('tsunami-hidden', !me._tsunamiHasUnread);
    }

    openTsunamiDetail(eventId = null, stationCode = '') {
        const me = this,
            latestTsunami = me.getLatestTsunami(),
            targetEventId = eventId || me.selectedTsunamiEventId || (latestTsunami && latestTsunami.eventid);

        if (!targetEventId) {
            return;
        }

        me.restoreCameraAfterTyphoonDisplay();
        me.closeOtherDisasterDetails('tsunami');
        me.selectTsunami(targetEventId, stationCode);
        me.syncTsunamiPanel();
        if (!me.tsunamiPanel.isOpen()) {
            me.tsunamiPanel.addTo(me);
        } else {
            me.tsunamiPanel.updateContent();
        }
        me.updateDisasterNotificationButtons();

        me._tsunamiMarkersVisible = true;
        me.renderTsunamiMarkers();
        me.moveToTsunamiSelection(targetEventId, stationCode);
    }

    clearTsunamiMarkers() {
        const me = this;

        if (me._tsunamiMarkers) {
            me._tsunamiMarkers.forEach(marker => marker.remove());
        }
        me._tsunamiMarkers = [];
    }

    renderTsunamiMarkers() {
        const me = this,
            tsunami = me.getSelectedTsunami(),
            stations = isDisasterSelectAll(me.selectedTsunamiEventId) ?
                me.getAllTsunamiStations() :
                (tsunami?.mapStations || []),
            causeEarthquakes = isDisasterSelectAll(me.selectedTsunamiEventId) ?
                me.getAllTsunamiCauseEarthquakes() :
                (
                    tsunami?.causeEarthquake?.hasHypocenterCoordinate ?
                        [tsunami.causeEarthquake] :
                        []
                ),
            markerEntries = [
                ...stations.map(station => ({kind: 'station', station})),
                ...causeEarthquakes.map(causeEarthquake => ({kind: 'causeEarthquake', causeEarthquake}))
            ];

        me.clearTsunamiMarkers();

        if (!markerEntries.length || !me._tsunamiMarkersVisible) {
            return;
        }

        me._tsunamiMarkers = markerEntries.map(({kind, station, causeEarthquake}) => {
            const isCauseEarthquake = kind === 'causeEarthquake';
            const warningType = station?.wavetypename || '注意報・警報なし',
                warningBorderColor = station ? getTsunamiWarningBorderColor(station.warningRank) : '';
            const element = helpers.createElement('button', {
                className: isCauseEarthquake ? 'earthquake-hypocenter-marker' : 'tsunami-marker',
                type: 'button',
                title: isCauseEarthquake ?
                    `${causeEarthquake.hypocentername || '震源地不明'} 震源地` :
                    `${station.stationname} ${station.height_desc || '-'} ${warningType}`,
                innerHTML: isCauseEarthquake ?
                    '<span class="earthquake-hypocenter-cross" aria-hidden="true"></span>' :
                    `<span class="tsunami-marker-dot" style="background-color: ${getTsunamiMarkerFillColor(station.markerLevel)}; border-color: ${warningBorderColor};"></span>`
            });
            const marker = new Marker({element});

            marker
                .setLngLat(isCauseEarthquake ?
                    [causeEarthquake.hypocenterLongitude, causeEarthquake.hypocenterLatitude] :
                    [station.longitude, station.latitude])
                .setZIndex(isCauseEarthquake ? 2000 : 1000)
                .addTo(me);
            if (!isCauseEarthquake) {
                marker.on('click', () => {
                    if (isDisasterSelectAll(me.selectedTsunamiEventId) && station.eventid) {
                        me.openTsunamiDetail(station.eventid, station.stationcode);
                    } else {
                        me.setSelectedTsunamiStationCode(station.stationcode);
                    }
                });
            }

            return marker;
        });
    }

    initializeTyphoonFeature() {
        const me = this;

        if (!configs.apiUrl.typhoon) {
            return;
        }

        me.typhoonEvents = [];
        me.selectedTyphoonEventId = '';
        me._typhoonHasUnread = false;
        me._typhoonLayersVisible = false;
        me._hasLoadedTyphoonEvents = false;
        me._knownTyphoonEventIds = new Set();
        me.ensureTyphoonMapLayers();

        const notificationContainer = me.ensureDisasterNotificationContainer();

        const button = me._typhoonButton = helpers.createElement('button', {
            className: 'typhoon-button typhoon-hidden',
            type: 'button'
        }, notificationContainer);
        const text = helpers.createElement('span', {
            className: 'typhoon-button-text'
        }, button);

        me._typhoonButtonLabel = helpers.createElement('span', {
            className: 'typhoon-button-label',
            textContent: '台風解析・予報情報'
        }, text);
        me._typhoonButtonSummary = helpers.createElement('span', {
            className: 'typhoon-button-summary'
        }, text);
        me._typhoonButtonBadge = helpers.createElement('span', {
            className: 'typhoon-button-badge typhoon-hidden'
        }, button);

        button.addEventListener('click', () => {
            me.openTyphoonDetail();
        });

        me.typhoonUpdateIntervalId = startTyphoonUpdates(
            me.handleTyphoonUpdate.bind(me),
            TYPHOON_REFRESH_INTERVAL
        );
    }

    getLatestTyphoon() {
        return getInitialDisplayTargetTyphoons(this.typhoonEvents || [])[0] || null;
    }

    getSelectedTyphoon() {
        const me = this,
            typhoons = me.typhoonEvents || [];

        if (isDisasterSelectAll(me.selectedTyphoonEventId)) {
            return null;
        }
        return typhoons.find(typhoon => typhoon.eventid === me.selectedTyphoonEventId) || typhoons[0] || null;
    }

    getDefaultTyphoonSelectionCandidate(typhoons = this.typhoonEvents || []) {
        return getInitialDisplayTargetTyphoons(typhoons)[0] || null;
    }

    syncTyphoonPanel() {
        const me = this;

        me.typhoonPanel.setTyphoons(
            me.typhoonEvents || [],
            me.selectedTyphoonEventId || ''
        );
    }

    selectTyphoon(eventId) {
        const me = this;

        if (isDisasterSelectAll(eventId)) {
            me.selectedTyphoonEventId = DISASTER_SELECT_ALL;
            me.syncTyphoonPanel();

            if (me._typhoonLayersVisible) {
                me.renderTyphoonLayers();
            }
            return;
        }

        const typhoon = (me.typhoonEvents || []).find(item => item.eventid === eventId);

        if (!typhoon) {
            return;
        }

        me.selectedTyphoonEventId = typhoon.eventid;
        me.syncTyphoonPanel();

        if (me._typhoonLayersVisible) {
            me.renderTyphoonLayers();
        }
    }

    handleAnalyzeTyphoonUpdate(nextTyphoons) {
        const me = this,
            nextLatestTyphoon = me.getDefaultTyphoonSelectionCandidate(nextTyphoons),
            isInitialLoad = !me._hasLoadedTyphoonEvents;
        let hasNewEvent = false;

        if (!nextTyphoons.length) {
            me.typhoonEvents = [];
            me.selectedTyphoonEventId = '';
            me._typhoonHasUnread = false;
            me.syncTyphoonPanel();
            me.updateTyphoonButton();
            me._typhoonLayersVisible = false;
            me.renderTyphoonLayers();
            if (me.typhoonPanel && me.typhoonPanel.isOpen()) {
                me.typhoonPanel.remove();
            }
            me._hasLoadedTyphoonEvents = true;
            me.markInitialDisasterFetchComplete('typhoon');
            return;
        }

        if (isInitialLoad) {
            hasNewEvent = !!nextLatestTyphoon;
        } else {
            hasNewEvent = nextTyphoons.some(typhoon =>
                typhoon.isInitialDisplayTarget &&
                typhoon.eventid &&
                !me._knownTyphoonEventIds.has(typhoon.eventid)
            );
        }

        nextTyphoons.forEach(typhoon => {
            if (typhoon.eventid) {
                me._knownTyphoonEventIds.add(typhoon.eventid);
            }
        });

        me.typhoonEvents = nextTyphoons;
        me._typhoonHasUnread = me._typhoonHasUnread || hasNewEvent;
        me._hasLoadedTyphoonEvents = true;
        if (
            (!me.typhoonPanel.isOpen() && !isDisasterSelectAll(me.selectedTyphoonEventId)) ||
            !nextTyphoons.some(typhoon => typhoon.eventid === me.selectedTyphoonEventId)
        ) {
            if (!isDisasterSelectAll(me.selectedTyphoonEventId)) {
                me.selectedTyphoonEventId = nextLatestTyphoon ? nextLatestTyphoon.eventid : '';
            }
        }

        me.syncTyphoonPanel();
        me.updateTyphoonButton();

        if (me.typhoonPanel.isOpen()) {
            me.typhoonPanel.updateContent();
        }
        if (me._typhoonLayersVisible) {
            me.renderTyphoonLayers();
        }
        me.markInitialDisasterFetchComplete('typhoon');
    }

    handleTyphoonUpdate(typhoonPayload) {
        const nextTyphoons = Array.isArray(typhoonPayload) ?
            typhoonPayload :
            (Array.isArray(typhoonPayload?.typhoons) ? typhoonPayload.typhoons : []);

        this.handleAnalyzeTyphoonUpdate(nextTyphoons);
    }

    updateTyphoonButton() {
        const me = this,
            button = me._typhoonButton,
            label = me._typhoonButtonLabel,
            summary = me._typhoonButtonSummary,
            badge = me._typhoonButtonBadge,
            latestTyphoon = me.getLatestTyphoon();

        if (!button || !label || !summary || !badge) {
            return;
        }

        if (!latestTyphoon) {
            button.classList.add('typhoon-hidden');
            badge.classList.add('typhoon-hidden');
            summary.textContent = '';
            label.textContent = '台風解析・予報情報';
            button.classList.remove('typhoon-button-new');
            return;
        }

        if (me.hasOpenDisasterDetail()) {
            button.classList.add('typhoon-hidden');
            badge.classList.add('typhoon-hidden');
            return;
        }

        label.textContent = me._typhoonHasUnread ?
            '台風解析・予報情報 NEW' :
            '台風解析・予報情報';
        summary.textContent = buildTyphoonButtonSummary(latestTyphoon);
        button.classList.remove('typhoon-hidden');
        button.classList.toggle('typhoon-button-new', me._typhoonHasUnread);
        badge.classList.toggle('typhoon-hidden', !me._typhoonHasUnread);
    }

    openTyphoonDetail(eventId = null) {
        const me = this,
            latestTyphoon = me.getLatestTyphoon(),
            selectedTyphoon = (me.typhoonEvents || []).find(typhoon => typhoon.eventid === me.selectedTyphoonEventId) || null,
            fallbackTyphoon = latestTyphoon || selectedTyphoon,
            targetTyphoon = eventId ?
                ((me.typhoonEvents || []).find(typhoon => typhoon.eventid === eventId) || null) :
                (
                    selectedTyphoon && selectedTyphoon.isInitialDisplayTarget ?
                        selectedTyphoon :
                        fallbackTyphoon
                ),
            targetEventId = targetTyphoon && targetTyphoon.eventid,
            shouldReopenTyphoonPanel = !me.typhoonPanel.isOpen() || me.typhoonPanel.isClosing();

        if (!targetEventId || !targetTyphoon) {
            return;
        }

        me.rememberCameraBeforeTyphoonDisplay();
        me.closeOtherDisasterDetails('typhoon');
        me.selectTyphoon(targetEventId);
        if (shouldReopenTyphoonPanel) {
            me.typhoonPanel.addTo(me);
        } else {
            me.typhoonPanel.updateContent();
        }
        me._typhoonHasUnread = false;
        me._typhoonLayersVisible = true;
        me.renderTyphoonLayers();
        me.updateDisasterNotificationButtons();
        me.moveToTyphoonSelection(targetEventId);
    }

    ensureTyphoonMapLayers() {
        const map = this.map;

        if (!map.getSource(TYPHOON_SOURCE_ID)) {
            map.addSource(TYPHOON_SOURCE_ID, {
                type: 'geojson',
                data: createEmptyTyphoonFeatureCollection()
            });
        }
        if (!map.getLayer(TYPHOON_STRONG_WIND_FILL_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_STRONG_WIND_FILL_LAYER_ID,
                type: 'fill',
                source: TYPHOON_SOURCE_ID,
                filter: ['all',
                    ['==', ['get', 'kind'], 'wind-area'],
                    ['==', ['get', 'areaType'], 'strong-wind']
                ],
                paint: {
                    'fill-color': '#ffd84d',
                    'fill-opacity': 0.1
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_STRONG_WIND_LINE_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_STRONG_WIND_LINE_LAYER_ID,
                type: 'line',
                source: TYPHOON_SOURCE_ID,
                filter: ['all',
                    ['==', ['get', 'kind'], 'wind-area'],
                    ['==', ['get', 'areaType'], 'strong-wind']
                ],
                paint: {
                    'line-color': '#ffe680',
                    'line-opacity': 0.8,
                    'line-width': 1.5
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_STORM_AREA_FILL_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_STORM_AREA_FILL_LAYER_ID,
                type: 'fill',
                source: TYPHOON_SOURCE_ID,
                filter: ['all',
                    ['==', ['get', 'kind'], 'wind-area'],
                    ['==', ['get', 'areaType'], 'storm-area']
                ],
                paint: {
                    'fill-color': '#ff5c5c',
                    'fill-opacity': 0.16
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_STORM_AREA_LINE_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_STORM_AREA_LINE_LAYER_ID,
                type: 'line',
                source: TYPHOON_SOURCE_ID,
                filter: ['all',
                    ['==', ['get', 'kind'], 'wind-area'],
                    ['==', ['get', 'areaType'], 'storm-area']
                ],
                paint: {
                    'line-color': '#ff8f8f',
                    'line-opacity': 0.88,
                    'line-width': 2
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_STORM_WARNING_LINE_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_STORM_WARNING_LINE_LAYER_ID,
                type: 'line',
                source: TYPHOON_SOURCE_ID,
                filter: ['all',
                    ['==', ['get', 'kind'], 'wind-area'],
                    ['==', ['get', 'areaType'], 'storm-warning']
                ],
                paint: {
                    'line-color': '#ff8f8f',
                    'line-opacity': 0.88,
                    'line-width': 2
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_PROBABILITY_OUTLINE_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_PROBABILITY_OUTLINE_LAYER_ID,
                type: 'line',
                source: TYPHOON_SOURCE_ID,
                filter: ['==', ['get', 'kind'], 'probability-outline'],
                paint: {
                    'line-color': '#ffffff',
                    'line-opacity': 0.95,
                    'line-width': 1.5
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_PROBABILITY_CIRCLE_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_PROBABILITY_CIRCLE_LAYER_ID,
                type: 'line',
                source: TYPHOON_SOURCE_ID,
                filter: ['==', ['get', 'kind'], 'probability-circle'],
                paint: {
                    'line-color': '#ffffff',
                    'line-opacity': 0.95,
                    'line-width': 1.25,
                    'line-dasharray': [2, 2]
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_TRACK_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_TRACK_LAYER_ID,
                type: 'line',
                source: TYPHOON_SOURCE_ID,
                filter: ['==', ['get', 'kind'], 'track'],
                paint: {
                    'line-color': '#ffd166',
                    'line-opacity': 0.95,
                    'line-width': 2.5
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_FORECAST_POINT_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_FORECAST_POINT_LAYER_ID,
                type: 'circle',
                source: TYPHOON_SOURCE_ID,
                filter: ['all',
                    ['==', ['get', 'kind'], 'point'],
                    ['==', ['get', 'pointType'], 'forecast']
                ],
                paint: {
                    'circle-radius': 4.5,
                    'circle-color': '#ffd166',
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 1.5
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_CURRENT_POINT_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_CURRENT_POINT_LAYER_ID,
                type: 'circle',
                source: TYPHOON_SOURCE_ID,
                filter: ['all',
                    ['==', ['get', 'kind'], 'point'],
                    ['==', ['get', 'pointType'], 'current']
                ],
                paint: {
                    'circle-radius': 6,
                    'circle-color': '#ff7b7b',
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 2
                }
            }, 'poi');
        }
        if (!map.getLayer(TYPHOON_LABEL_LAYER_ID)) {
            this.addLayer({
                id: TYPHOON_LABEL_LAYER_ID,
                type: 'symbol',
                source: TYPHOON_SOURCE_ID,
                filter: ['==', ['get', 'kind'], 'label'],
                layout: {
                    'text-field': ['get', 'label'],
                    'text-size': 12,
                    'text-offset': [0, 1.1],
                    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                    'text-allow-overlap': true
                },
                paint: {
                    'text-color': '#ffffff',
                    'text-halo-color': 'rgba(20, 25, 38, 0.95)',
                    'text-halo-width': 1.25
                }
            }, 'poi');
        }
    }

    renderTyphoonLayers() {
        const selectedTyphoon = this.getSelectedTyphoon(),
            source = this.map.getSource(TYPHOON_SOURCE_ID);

        if (!source) {
            return;
        }

        source.setData(
            this._typhoonLayersVisible ?
                (
                    isDisasterSelectAll(this.selectedTyphoonEventId) ?
                        mergeTyphoonFeatureCollections(this.typhoonEvents || []) :
                        (selectedTyphoon?.mapFeatureCollection || createEmptyTyphoonFeatureCollection())
                ) :
                createEmptyTyphoonFeatureCollection()
        );
    }

    initializeEarthquakeFeature() {
        const me = this;

        if (!configs.apiUrl.earthquake) {
            return;
        }

        me._earthquakeMarkers = [];
        me._earthquakeMarkersVisible = false;
        me._earthquakeHasUnread = false;
        me.earthquakeEvents = [];
        me.selectedEarthquakeEventId = '';

        const notificationContainer = me.ensureDisasterNotificationContainer();

        const button = me._earthquakeButton = helpers.createElement('button', {
            className: 'earthquake-button earthquake-hidden',
            type: 'button'
        }, notificationContainer);
        const text = helpers.createElement('span', {
            className: 'earthquake-button-text'
        }, button);

        me._earthquakeButtonLabel = helpers.createElement('span', {
            className: 'earthquake-button-label',
            textContent: '地震情報'
        }, text);
        me._earthquakeButtonSummary = helpers.createElement('span', {
            className: 'earthquake-button-summary'
        }, text);
        me._earthquakeButtonBadge = helpers.createElement('span', {
            className: 'earthquake-button-badge earthquake-hidden'
        }, button);

        button.addEventListener('click', () => {
            me.openEarthquakeDetail();
        });

        me.earthquakeUpdateIntervalId = startEarthquakeUpdates(
            me.handleEarthquakeUpdate.bind(me),
            EARTHQUAKE_REFRESH_INTERVAL
        );
    }

    getLatestEarthquake() {
        return (this.earthquakeEvents || [])[0] || null;
    }

    getSelectedEarthquake() {
        const me = this,
            earthquakes = me.earthquakeEvents || [];

        if (isDisasterSelectAll(me.selectedEarthquakeEventId)) {
            return null;
        }
        return earthquakes.find(earthquake => earthquake.eventid === me.selectedEarthquakeEventId) || earthquakes[0] || null;
    }

    hasRecentEarthquakes(earthquakes = this.earthquakeEvents || []) {
        const now = Date.now();

        return earthquakes.some(earthquake => {
            const candidate = earthquake && (
                earthquake.occurrence_datetime ||
                earthquake.origintime ||
                earthquake.report_datetime
            );
            const timestamp = candidate ? new Date(candidate).getTime() : NaN;

            return Number.isFinite(timestamp) && now >= timestamp && now - timestamp <= EARTHQUAKE_NEW_WINDOW;
        });
    }

    syncEarthquakePanel() {
        const me = this;

        me.earthquakePanel.setEarthquakes(
            me.earthquakeEvents || [],
            me.selectedEarthquakeEventId || ''
        );
    }

    selectEarthquake(eventId) {
        const me = this;

        if (isDisasterSelectAll(eventId)) {
            me.selectedEarthquakeEventId = DISASTER_SELECT_ALL;
            me.syncEarthquakePanel();

            if (me._earthquakeMarkersVisible) {
                me.renderEarthquakeMarkers();
            }
            return;
        }

        const earthquake = (me.earthquakeEvents || []).find(item => item.eventid === eventId);

        if (!earthquake) {
            return;
        }

        me.selectedEarthquakeEventId = earthquake.eventid;
        me.syncEarthquakePanel();

        if (me._earthquakeMarkersVisible) {
            me.renderEarthquakeMarkers();
        }
    }

    handleEarthquakeUpdate(earthquakes) {
        const me = this,
            nextEarthquakes = Array.isArray(earthquakes) ? earthquakes : [],
            nextLatestEarthquake = nextEarthquakes[0] || null;

        if (!nextEarthquakes.length) {
            me.earthquakeEvents = [];
            me.selectedEarthquakeEventId = '';
            me._earthquakeHasUnread = false;
            me.syncEarthquakePanel();
            me.updateEarthquakeButton();
            me.clearEarthquakeMarkers();
            me._earthquakeMarkersVisible = false;
            if (me.earthquakePanel && me.earthquakePanel.isOpen()) {
                me.earthquakePanel.remove();
            }
            me.markInitialDisasterFetchComplete('earthquake');
            return;
        }

        me.earthquakeEvents = nextEarthquakes;
        me._earthquakeHasUnread = me.hasRecentEarthquakes(nextEarthquakes);
        if (
            (!me.earthquakePanel.isOpen() && !isDisasterSelectAll(me.selectedEarthquakeEventId)) ||
            !nextEarthquakes.some(earthquake => earthquake.eventid === me.selectedEarthquakeEventId)
        ) {
            if (!isDisasterSelectAll(me.selectedEarthquakeEventId)) {
                me.selectedEarthquakeEventId = nextLatestEarthquake.eventid;
            }
        }
        me.syncEarthquakePanel();

        me.updateEarthquakeButton();

        if (me.earthquakePanel.isOpen()) {
            me.earthquakePanel.updateContent();
        }
        if (me._earthquakeMarkersVisible) {
            me.renderEarthquakeMarkers();
        }
        me.markInitialDisasterFetchComplete('earthquake');
    }

    updateEarthquakeButton() {
        const me = this,
            button = me._earthquakeButton,
            label = me._earthquakeButtonLabel,
            summary = me._earthquakeButtonSummary,
            badge = me._earthquakeButtonBadge,
            latestEarthquake = me.getLatestEarthquake();

        if (!button || !label || !summary || !badge) {
            return;
        }

        if (!latestEarthquake) {
            button.classList.add('earthquake-hidden');
            badge.classList.add('earthquake-hidden');
            summary.textContent = '';
            label.textContent = '地震情報';
            button.classList.remove('earthquake-button-new');
            return;
        }

        if (me.hasOpenDisasterDetail()) {
            button.classList.add('earthquake-hidden');
            badge.classList.add('earthquake-hidden');
            return;
        }

        label.textContent = me._earthquakeHasUnread ? '地震情報 NEW' : '地震情報';
        summary.textContent = buildEarthquakeButtonSummary(latestEarthquake);
        button.classList.remove('earthquake-hidden');
        button.classList.toggle('earthquake-button-new', me._earthquakeHasUnread);
        badge.classList.toggle('earthquake-hidden', !me._earthquakeHasUnread);
    }

    markEarthquakeAsNew() {
        this._earthquakeHasUnread = this.hasRecentEarthquakes();
        this.updateEarthquakeButton();
    }

    clearEarthquakeNotification() {
        this._earthquakeHasUnread = this.hasRecentEarthquakes();
        this.updateEarthquakeButton();
    }

    openEarthquakeDetail(eventId = null) {
        const me = this,
            latestEarthquake = me.getLatestEarthquake(),
            targetEventId = eventId || me.selectedEarthquakeEventId || (latestEarthquake && latestEarthquake.eventid);

        if (!targetEventId) {
            return;
        }

        me.restoreCameraAfterTyphoonDisplay();
        me.closeOtherDisasterDetails('earthquake');
        me.selectEarthquake(targetEventId);
        if (!me.earthquakePanel.isOpen()) {
            me.earthquakePanel.addTo(me);
        } else {
            me.earthquakePanel.updateContent();
        }
        me.updateDisasterNotificationButtons();

        me._earthquakeMarkersVisible = true;
        me.renderEarthquakeMarkers();
        me.moveToEarthquakeSelection(targetEventId);
    }

    clearEarthquakeMarkers() {
        const me = this;

        if (me._earthquakeMarkers) {
            me._earthquakeMarkers.forEach(marker => marker.remove());
        }
        me._earthquakeMarkers = [];
    }

    renderEarthquakeMarkers() {
        const me = this,
            earthquake = me.getSelectedEarthquake(),
            markerEntries = isDisasterSelectAll(me.selectedEarthquakeEventId) ?
                (me.earthquakeEvents || []).flatMap(currentEarthquake => [
                    ...currentEarthquake.mapMunicipalities.map(municipality => ({
                        kind: 'municipality',
                        earthquake: currentEarthquake,
                        municipality
                    })),
                    ...(
                        currentEarthquake.hasHypocenterCoordinate ?
                            [{
                                kind: 'hypocenter',
                                earthquake: currentEarthquake
                            }] :
                            []
                    )
                ]) :
                (
                    earthquake ?
                        [
                            ...earthquake.mapMunicipalities.map(municipality => ({
                                kind: 'municipality',
                                earthquake,
                                municipality
                            })),
                            ...(
                                earthquake.hasHypocenterCoordinate ?
                                    [{
                                        kind: 'hypocenter',
                                        earthquake
                                    }] :
                                    []
                            )
                        ] :
                        []
                );

        me.clearEarthquakeMarkers();

        if (!markerEntries.length || !me._earthquakeMarkersVisible) {
            return;
        }

        me._earthquakeMarkers = markerEntries.map(({kind, earthquake: currentEarthquake, municipality}) => {
            const isHypocenter = kind === 'hypocenter',
                element = helpers.createElement('button', {
                    className: isHypocenter ? 'earthquake-hypocenter-marker' : 'earthquake-marker',
                    type: 'button',
                    title: isHypocenter ?
                        `${currentEarthquake.hypocentername || '震源地不明'} 震源地` :
                        `${municipality.prefname}${municipality.cityname} 震度${municipality.intensity}`,
                    innerHTML: isHypocenter ?
                        '<span class="earthquake-hypocenter-cross" aria-hidden="true"></span>' :
                        `<span class="earthquake-marker-badge" style="background-color: ${getEarthquakeIntensityColor(municipality.rawIntensity)};">${escapeHTML(municipality.intensity || '-')}</span>`
                }),
                marker = new Marker({element}),
                intensityRank = municipality ? getEarthquakeIntensityRank(municipality.rawIntensity) : 0,
                lngLat = isHypocenter ?
                    [currentEarthquake.hypocenterLongitude, currentEarthquake.hypocenterLatitude] :
                    [municipality.longitude, municipality.latitude];

            marker
                .setLngLat(lngLat)
                .setZIndex(isHypocenter ? 2000 : 1000 + intensityRank)
                .addTo(me);
            marker.on('click', () => {
                me.openEarthquakeDetail(currentEarthquake.eventid);
            });

            return marker;
        });
    }

    _jumpTo(options) {
        const me = this,
            {map, trackingMode, trackingParams, frameRateFactor} = me,
            now = performance.now(),
            currentZoom = map.getZoom(),
            currentBearing = map.getBearing(),
            currentPitch = map.getPitch(),
            scrollZooming = map.scrollZoom._active;
        let zoom, pitch,
            {center, altitude, bearing, easeOutFactor, easeInFactor, bearingFactor} = options;

        if (trackingMode === 'position') {
            zoom = me.baseZoom;
            bearing = currentBearing;
            pitch = currentPitch;
        } else if (trackingMode === 'helicopter') {
            zoom = 15;
            bearing = trackingParams.bearing.fn(now);
            pitch = 60;
        } else if (trackingMode === 'drone') {
            zoom = 17;
            bearing = trackingParams.bearing.fn(now);
            pitch = 75;
        } else if (trackingMode === 'bird') {
            me.updateTrackingParams();
            zoom = trackingParams.zoom.fn(now);
            bearing = trackingParams.bearing.fn(now);
            pitch = trackingParams.pitch.fn(now);
        } else {
            if (trackingMode === 'front' || trackingMode === 'topfront') {
                bearing = (bearing + 360) % 360 - 180;
            }
            if (bearingFactor >= 0) {
                bearing = currentBearing + ((bearing - currentBearing + 540) % 360 - 180) * bearingFactor * frameRateFactor;
            }
            if (trackingMode === 'back' || trackingMode === 'front') {
                zoom = 18.5;
                pitch = 85;
            } else {
                zoom = 15;
                pitch = 60;
            }
        }

        const cameraToGround = map.getFreeCameraOptions().position.z / Math.cos(currentPitch * DEGREE_TO_RADIAN),
            cameraToObject = cameraToGround * Math.pow(2, currentZoom - zoom),
            objectToGround = me.getModelPosition(center, altitude).z / Math.cos(pitch * DEGREE_TO_RADIAN);

        // Adjust zoom according to the altitude of the object
        zoom = Math.min(zoom - Math.log2(Math.max(cameraToObject + objectToGround, 0) / cameraToObject), 22);

        center = me.adjustCoord(center, altitude, bearing);
        if (easeOutFactor >= 0) {
            const currentCenter = map.getCenter();

            center = new LngLat(
                helpers.lerp(currentCenter.lng, center.lng, easeOutFactor * frameRateFactor),
                helpers.lerp(currentCenter.lat, center.lat, easeOutFactor * frameRateFactor)
            );
        }
        if (easeInFactor >= 0) {
            zoom = helpers.lerp(currentZoom, zoom, easeInFactor * frameRateFactor);
            pitch = helpers.lerp(currentPitch, pitch, easeInFactor * frameRateFactor);
        }

        // Prevent layer switch due to calculation error
        if (Math.floor(zoom + 1) - zoom < 1e-6) {
            zoom = Math.floor(zoom + 1);
        }

        // Prevent layer switch back and forth
        if (me.prevLayerZoom === getLayerZoom(zoom)) {
            zoom = currentZoom;
        } else {
            delete me.prevLayerZoom;
        }

        map.jumpTo({center, zoom, bearing, pitch}, {tracking: true});

        // Workaround for the issue of the scroll zoom during tracking
        if (scrollZooming) {
            map.scrollZoom._active = true;
        }
    }

    updateVisibleArea() {
        const me = this,
            map = me.map,
            {width, height} = map.transform,
            topLeft = me.getModelPosition(map.unproject([0, 0])),
            topRight = me.getModelPosition(map.unproject([width, 0])),
            bottomLeft = me.getModelPosition(map.unproject([0, height])),
            bottomRight = me.getModelPosition(map.unproject([width, height]));

        me.visibleArea = helpers.bufferTrapezoid([
            [topLeft.x, topLeft.y],
            [topRight.x, topRight.y],
            [bottomRight.x, bottomRight.y],
            [bottomLeft.x, bottomLeft.y]
        ], Math.max(1.4e-5, 5e-5 * Math.sin(map.getPitch() * DEGREE_TO_RADIAN)));
    }

    updateTrainProps(train) {
        const me = this,
            feature = train.railwayFeature = me.featureLookup.get(`${train.r.id}.${me.layerZoom}`),
            stationOffsets = feature.properties['station-offsets'],
            sectionIndex = train.sectionIndex,
            offset = train.offset = stationOffsets[sectionIndex];

        train.interval = stationOffsets[sectionIndex + train.sectionLength] - offset;
    }

    updateTrainShape(train, t) {
        const me = this,
            {map, trafficLayer} = me,

            // Deprecated
            // objectUnit = me.objectUnit,
            objectUnit = 0,
            carComposition = 1,

            {railwayFeature: feature, direction, cars, animationID} = train,
            length = cars.length;
        let marked, tracked, viewMode;

        if (t !== undefined) {
            train._t = t;
        }
        if (train._t === undefined) {
            return;
        }

        if (length === 0) {
            const {markedObject, trackedObject} = me,
                id = train.id,
                car = {
                    type: 'train',
                    object: train,
                    index: 0,
                    color: (train.v || train.r).color,
                    delay: 0
                };

            cars.push(car);

            // Reset marked/tracked object if it was marked/tracked before
            // Delay calling markObject() and trackObject() as they require the object position to be set
            if (markedObject && markedObject.type === 'train' && markedObject.object.id === id) {
                marked = cars[markedObject.index];
            }
            if (trackedObject && trackedObject.type === 'train' && trackedObject.object.id === id) {
                tracked = cars[trackedObject.index];
            }
            if (me.selection === id) {
                tracked = cars[0];
                delete me.selection;
            }
        }

        cars[0].delay = train.delay ? 1 : 0;

        const pArr = helpersGeojson.getCoordAndBearing(feature, train.offset + train._t * train.interval, carComposition, objectUnit);
        for (let i = 0, ilen = cars.length; i < ilen; i++) {
            const car = cars[i],
                {coord, altitude, bearing, pitch} = pArr[i];

            if (car.altitude < 0 && altitude >= 0) {
                viewMode = 'ground';
            } else if (car.altitude >= 0 && altitude < 0) {
                viewMode = 'underground';
            }

            car.coord = coord;
            car.altitude = altitude;
            car.bearing = bearing + (direction < 0 ? 180 : 0);
            car.pitch = pitch * direction;

            if (marked === car) {
                me.markObject(car);
            }
            if (tracked === car) {
                me.trackObject(car);
            }
            if (me.markedObject === car) {
                car.outline = 1;
            } else if (me.trackedObject === car) {
                car.outline = helpers.blink();
            } else {
                car.outline = 0;
            }

            if (me.trackedObject === car && !me.viewAnimationID && !map._zooming && !map._rotating && !map._pitching) {
                me._jumpTo({center: coord, altitude, bearing: car.bearing, bearingFactor: .02});
            }

            // Reduce the frame rate of invisible objects for performance optimization
            if (animation.isActive(animationID)) {
                const {x, y} = me.getModelPosition(coord),
                    frameRate = helpers.pointInTrapezoid([x, y], me.visibleArea) ? me.ecoMode === 'normal' && map._loaded ? 0 : me.ecoFrameRate : 1;

                animation.setFrameRate(animationID, frameRate);
            }

            if (car.meshIndex === undefined) {
                trafficLayer.addObject(car);
            } else {
                trafficLayer.updateObject(car);
            }
            if (viewMode && me.trackedObject === car) {
                me._setViewMode(viewMode);
            }

            if (me.trackedObject === car && me.markedObject === car) {
                me.updatePopup();
            }
        }
    }

    updateFlightShape(flight, t) {
        const me = this,
            {map, trafficLayer} = me,
            {feature, animationID} = flight;
        let aircraft = flight.aircraft,
            tracked;

        if (t !== undefined) {
            flight._t = t;
        }
        if (flight._t === undefined) {
            return;
        }
        if (!aircraft) {
            const operator = flight.a;

            aircraft = flight.aircraft = {
                type: 'flight',
                object: flight,
                color: [operator.color || '#FFFFFF', operator.tailcolor || '#FFFFFF']
            };

            // Set tracked object if the selection is specified
            // Delay calling trackObject() as they require the object position to be set
            if (me.selection === flight.id) {
                tracked = aircraft;
                delete me.selection;
            }
        }

        const {coord, altitude, bearing, pitch} = helpersGeojson.getCoordAndBearing(feature, flight._t * feature.properties.length, 1, 0)[0];

        aircraft.coord = coord;
        aircraft.altitude = altitude;
        aircraft.bearing = bearing;
        aircraft.pitch = pitch;

        if (tracked === aircraft) {
            me.trackObject(aircraft);
        }
        if (me.markedObject === aircraft) {
            aircraft.outline = 1;
        } else if (me.trackedObject === aircraft) {
            aircraft.outline = helpers.blink();
        } else {
            aircraft.outline = 0;
        }

        if (me.trackedObject === aircraft && !me.viewAnimationID && !map._zooming && !map._rotating && !map._pitching) {
            me._jumpTo({center: coord, altitude, bearing, bearingFactor: .02});
        }

        // Reduce the frame rate of invisible objects for performance optimization
        if (animation.isActive(animationID)) {
            const {x, y} = me.getModelPosition(coord),
                frameRate = helpers.pointInTrapezoid([x, y], me.visibleArea) || me.trackedObject === aircraft ? me.ecoMode === 'normal' && map._loaded ? 0 : me.ecoFrameRate : 1;

            animation.setFrameRate(animationID, frameRate);
        }

        if (aircraft.meshIndex === undefined) {
            trafficLayer.addObject(aircraft);
        } else {
            trafficLayer.updateObject(aircraft);
        }

        if (me.trackedObject === aircraft && me.markedObject === aircraft) {
            me.updatePopup();
        }
    }

    updateBusProps(bus) {
        const sectionIndex = bus.sectionIndex,
            stopOffsets = bus.offsets,
            offset = bus.offset = stopOffsets[sectionIndex];

        bus.interval = stopOffsets[sectionIndex + bus.sectionLength] - offset;
    }

    updateBusShape(bus, t) {
        const me = this,
            {map, trafficLayer} = me,
            animationID = bus.animationID;
        let car = bus.car;

        if (t !== undefined) {
            bus._t = t;
        }
        if (bus._t === undefined) {
            return;
        }

        if (car === undefined) {
            car = bus.car = {
                type: 'bus',
                object: bus,
                color: me.gtfs.get(bus.gtfsId).color
            };
        }

        const {coord, altitude, bearing, pitch} = helpersGeojson.getCoordAndBearing(bus.feature, bus.offset + bus._t * bus.interval, 1, 0)[0];

        car.coord = coord;
        car.altitude = altitude;
        car.bearing = bearing;
        car.pitch = pitch;

        if (me.markedObject === car) {
            car.outline = 1;
        } else if (me.trackedObject === car) {
            car.outline = helpers.blink();
        } else {
            car.outline = 0;
        }

        if (me.trackedObject === car && !me.viewAnimationID && !map._zooming && !map._rotating && !map._pitching) {
            me._jumpTo({center: coord, altitude, bearing: car.bearing, bearingFactor: .02});
        }

        // Reduce the frame rate of invisible objects for performance optimization
        if (animation.isActive(animationID)) {
            const {x, y} = me.getModelPosition(coord),
                frameRate = helpers.pointInTrapezoid([x, y], me.visibleArea) || me.trackedObject === car ? me.ecoMode === 'normal' && map._loaded ? 0 : me.ecoFrameRate : 1;

            animation.setFrameRate(animationID, frameRate);
        }

        if (car.meshIndex === undefined) {
            trafficLayer.addObject(car);
        } else {
            trafficLayer.updateObject(car);
        }

        if (me.trackedObject === car && me.markedObject === car) {
            me.updatePopup();
        }
    }

    refreshTrains() {
        // 公共交通機能を無効化のため全体をコメントアウト
        // const me = this,
        //     initialSelection = me.initialSelection,
        //     now = me.clock.getTimeOffset();

        // for (const timetable of me.timetables.getAll()) {
        //     if (timetable.start <= now && now <= timetable.end && !me.standbyTrainLookup.has(timetable.id)) {
        //         me.trainStart(new Train(timetable));
        //     }
        // }

        // me.trafficLayer.refreshDelayMarkers();

        // me.trainLoaded = true;
        // if (initialSelection) {
        //     me.setSelection(initialSelection);
        // }
        return;
    }

    trainStart(train, index) {
        const me = this,
            {r: railway, timetable} = train,
            now = me.clock.getTimeOffset();

        if (me.checkActiveTrains(train) || (railway.status && railway.dynamic && !me.realtimeTrains.has(train.id)) || railway.suspended) {
            me.deactivateTrain(train);
            return;
        }
        if (!me.setSectionData(train, index)) {
            me.deactivateTrain(train);
            return; // Out of range
        }
        me.activeTrainLookup.set(train.id, train);
        train.cars = [];
        me.updateTrainProps(train);

        const departureTime = train.departureTime + (train.delay || 0);

        if (!timetable && train.sectionLength !== 0) {
            me.trainRepeat(train);
        } else if (timetable && now >= departureTime) {
            me.trainRepeat(train, now - departureTime);
        } else {
            me.trainStand(train);
        }
    }

    trainStand(train, final) {
        const me = this,
            timetable = train.timetable;

        if (!timetable) {
            final = !me.setSectionData(train, undefined, !me.realtimeTrains.has(train.id));
        }

        if (!final && train.arrivalStation) {
            me.updateTrainProps(train);
            me.updateTrainShape(train, 0);
        }

        if (!timetable && train.sectionLength !== 0) {
            me.trainRepeat(train);
        } else {
            const markedObject = me.markedObject,
                clock = me.clock,
                departureTime = train.departureTime + (train.delay || 0),
                minStandingDuration = configs.minStandingDuration;

            train.standing = true;
            if (markedObject && markedObject.object === train) {
                me.updatePopup({setHTML: true});
            }
            train.animationID = animation.start({
                callback: () => {
                    const trackedObject = me.trackedObject;

                    if (trackedObject && trackedObject.object === train) {
                        me.updateTrainShape(train);
                    }
                },
                complete: () => {
                    const clock = me.clock;

                    if (final) {
                        me.stopTrain(train);
                    } else if (timetable) {
                        // Specify elapsed time if clock speed is high because the time typically advances
                        me.trainRepeat(train, clock.speed === 1 ? undefined : clock.getTimeOffset() - departureTime);
                    } else {
                        me.trainStand(train);
                    }
                },
                duration: timetable ?
                    // Set minimum duration to 0 if clock speed is high because the time typically advances
                    Math.max(departureTime - clock.getTimeOffset(), clock.speed === 1 ? minStandingDuration : 0) :
                    final ? minStandingDuration : configs.realtimeCheckInterval,
                clock
            });
        }
    }

    trainRepeat(train, elapsed) {
        const me = this,
            {clock, markedObject} = me,
            now = clock.getTimeOffset(),
            delay = train.delay || 0,
            minDelay = configs.minDelay,
            {arrivalTime, nextDepartureTime} = train;
        let minDuration, maxDuration;

        if (nextDepartureTime !== undefined) {
            maxDuration = nextDepartureTime + delay - now + (elapsed || 0) - minDelay + 60000 - configs.minStandingDuration;
        }
        if (arrivalTime !== undefined) {
            minDuration = arrivalTime + delay - now + (elapsed || 0) - minDelay;
            if (!(maxDuration < minDuration + 60000)) {
                maxDuration = minDuration + 60000;
            }
        }
        train.standing = false;
        if (markedObject && markedObject.object === train) {
            me.updatePopup({setHTML: true});
        }
        train.animationID = startTrainAnimation(t => {
            // Guard for an unexpected error
            // Probably a bug due to duplicate train IDs in timetable lookup
            if (!train.cars || isNaN(t)) {
                console.log('Invalid train', Object.assign({}, train));
                me.stopTrain(train);
                return;
            }

            me.updateTrainShape(train, t);
        }, () => {
            const activeTrainLookup = me.activeTrainLookup,
                {timetable, timetableIndex} = train;

            // Guard for an unexpected error
            // Probably a bug due to duplicate train IDs in timetable lookup
            if (!train.cars || (timetable && timetableIndex + 1 >= timetable.stations.length)) {
                me.stopTrain(train);
                return;
            }

            if (!me.setSectionData(train, timetableIndex + 1)) {
                const nextTimetables = timetable && timetable.nt;

                if (nextTimetables) {
                    let needToStand = false;

                    for (const {t: id} of nextTimetables[0].pt) {
                        const prevTrain = activeTrainLookup.get(id);

                        if (prevTrain && prevTrain.arrivalStation) {
                            needToStand = true;
                        }
                    }
                    if (needToStand) {
                        me.trainStand(train);
                    } else {
                        const {markedObject, trackedObject} = me;
                        let marked = false,
                            tracked = false;

                        for (const {t: id} of nextTimetables[0].pt) {
                            const prevTrain = activeTrainLookup.get(id);

                            if (markedObject && markedObject.object === prevTrain) {
                                marked = true;
                            }
                            if (trackedObject && trackedObject.object === prevTrain) {
                                tracked = true;
                            }
                            if (prevTrain) {
                                me.stopTrain(prevTrain, marked || tracked);
                            }
                        }
                        nextTimetables.forEach((nextTimetable, index) => {
                            const nextTrain = me.standbyTrainLookup.get(nextTimetable.id) || new Train(nextTimetable);

                            if (index === 0) {
                                if (marked) {
                                    markedObject.object = nextTrain;
                                }
                                if (tracked) {
                                    trackedObject.object = nextTrain;
                                }
                            }
                            me.trainStart(nextTrain, 0);
                        });
                    }
                    return;
                }
                me.trainStand(train, true);
            } else {
                me.trainStand(train);
            }
        }, Math.abs(train.interval), minDuration, maxDuration, elapsed, clock);
    }

    refreshFlights() {
        // 公共交通機能を無効化のため全体をコメントアウト
        // const me = this,
        //     {clock, activeFlightLookup, markedObject, initialSelection} = me,
        //     now = clock.getTimeOffset();

        // for (const flight of me.flightLookup.values()) {
        //     const {id, start} = flight;

        //     if (flight.entry <= now && now <= flight.end && !activeFlightLookup.has(id)) {
        //         activeFlightLookup.set(id, flight);
        //         if (now >= start) {
        //             me.flightRepeat(flight, now - start);
        //         } else {
        //             me.updateFlightShape(flight, 0);
        //             flight.standing = true;
        //             if (markedObject && markedObject.object === flight) {
        //                 me.updatePopup({setHTML: true});
        //             }
        //             flight.animationID = animation.start({
        //                 callback: () => {
        //                     const trackedObject = me.trackedObject;

        //                     if (trackedObject && trackedObject.object === flight) {
        //                         me.updateFlightShape(flight);
        //                     }
        //                 },
        //                 complete: () => {
        //                     me.flightRepeat(flight);
        //                 },
        //                 duration: start - now,
        //                 clock
        //             });
        //         }
        //     }
        // }

        // me.flightLoaded = true;
        // if (initialSelection) {
        //     me.setSelection(initialSelection);
        // }
        return;
    }

    flightRepeat(flight, elapsed) {
        const me = this,
            {clock, markedObject} = me;

        flight.standing = false;
        if (markedObject && markedObject.object === flight) {
            me.updatePopup({setHTML: true});
        }
        flight.animationID = startFlightAnimation(t => {
            me.updateFlightShape(flight, t);
        }, () => {
            const markedObject = me.markedObject;

            flight.standing = true;
            if (markedObject && markedObject.object === flight) {
                me.updatePopup({setHTML: true});
            }
            flight.animationID = animation.start({
                callback: () => {
                    const trackedObject = me.trackedObject;

                    if (trackedObject && trackedObject.object === flight) {
                        me.updateFlightShape(flight);
                    }
                },
                complete: () => {
                    me.stopFlight(flight);
                },
                duration: Math.max(flight.end - clock.getTimeOffset(), 0),
                clock
            });
        }, flight.feature.properties.length, flight.maxSpeed, flight.acceleration, elapsed, clock);
    }

    busStart(bus) {
        const me = this;

        if (!me.setBusSectionData(bus)) {
            return;
        }
        me.gtfs.get(bus.gtfsId).activeBusLookup.set(bus.trip.id, bus);
        me.updateBusProps(bus);

        // Sometimes bus.interval becomes 0 because the busroute coordinates
        // are incorrect and a few busstops shares the same coordinates.
        if (bus.sectionLength > 0 && bus.interval === 0) {
            console.log('bus zero interval:', bus);
        }

        if (bus.sectionLength > 0 && bus.interval > 0) {
            me.busRepeat(bus);
        } else {
            me.busStand(bus);
        }
    }

    busStand(bus) {
        const me = this;
        let final = false;

        if (me.setBusSectionData(bus, !me.gtfs.get(bus.gtfsId).realtimeBuses.has(bus.trip.id))) {
            me.updateBusProps(bus);
            me.updateBusShape(bus, 0);
        } else {
            final = true;
        }

        // Sometimes bus.interval becomes 0 because the busroute coordinates
        // are incorrect and a few busstops shares the same coordinates.
        if (bus.sectionLength > 0 && bus.interval === 0) {
            console.log('bus zero interval:', bus);
        }

        if (bus.sectionLength > 0 && bus.interval > 0) {
            me.busRepeat(bus);
        } else {
            const {markedObject, trackedObject} = me;

            if (markedObject && markedObject.object === bus) {
                me.updatePopup({setHTML: true});
            }
            if (trackedObject && trackedObject.object === bus && me.detailPanel) {
                me.detailPanel.updateHeader();
            }
            bus.animationID = animation.start({
                callback: () => {
                    const trackedObject = me.trackedObject;

                    if (trackedObject && trackedObject.object === bus) {
                        me.updateBusShape(bus);
                    }
                },
                complete: () => {
                    if (final) {
                        me.stopBus(bus);
                    } else {
                        me.busStand(bus);
                    }
                },
                duration: configs.realtimeCheckInterval,
                clock: me.clock
            });
        }
    }

    busRepeat(bus) {
        const me = this,
            {markedObject, trackedObject} = me;

        if (markedObject && markedObject.object === bus) {
            me.updatePopup({setHTML: true});
        }
        if (trackedObject && trackedObject.object === bus && me.detailPanel) {
            me.detailPanel.updateHeader();
        }
        bus.animationID = startBusAnimation(t => {
            me.updateBusShape(bus, t);
        }, () => {
            me.busStand(bus);
        }, Math.abs(bus.interval), 0, me.clock);
    }

    updateTrackingParams(reset) {
        const me = this,
            {map, trackingMode, trackingParams} = me,
            now = performance.now();

        if (trackingMode === 'bird') {
            const {zoom, bearing, pitch} = trackingParams;

            if (!zoom.time || reset) {
                const time = zoom.time = [0, now, 0, 0],
                    value = zoom.value = [0, me.baseZoom, 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 30000);
                    value[i] = Math.random() * 5 + 15;
                }
                zoom.fn = createInterpolant(time, value);
            } else if (now >= zoom.time[2]) {
                zoom.time = zoom.time.slice(1).concat(zoom.time[3] + Math.random() * 10000 + 30000);
                zoom.value = zoom.value.slice(1).concat(Math.random() * 5 + 15);
                zoom.fn = createInterpolant(zoom.time, zoom.value);
            }
            if (!bearing.time || reset) {
                const time = bearing.time = [0, now, 0, 0],
                    value = bearing.value = [0, map.getBearing(), 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 40000);
                    value[i] = Math.random() * 360 - 180;
                }
                bearing.fn = createInterpolant(time, value);
            } else if (now >= bearing.time[2]) {
                bearing.time = bearing.time.slice(1).concat(bearing.time[3] + Math.random() * 10000 + 40000);
                bearing.value = bearing.value.slice(1).concat(Math.random() * 360 - 180);
                bearing.fn = createInterpolant(bearing.time, bearing.value);
            }
            if (!pitch.time || reset) {
                const time = pitch.time = [0, now, 0, 0],
                    value = pitch.value = [0, map.getPitch(), 0, 0];
                for (const [i, j] of [[0, 1], [2, 1], [3, 2]]) {
                    time[i] = time[j] + Math.sign(i - j) * (Math.random() * 10000 + 20000);
                    value[i] = Math.random() * 30 + 45;
                }
                pitch.fn = createInterpolant(time, value);
            } else if (now >= pitch.time[2]) {
                pitch.time = pitch.time.slice(1).concat(pitch.time[3] + Math.random() * 10000 + 20000);
                pitch.value = pitch.value.slice(1).concat(Math.random() * 30 + 45);
                pitch.fn = createInterpolant(pitch.time, pitch.value);
            }
        } else {
            delete trackingParams.zoom.time;
            delete trackingParams.bearing.time;
            delete trackingParams.pitch.time;
            if (trackingMode === 'drone') {
                const bearing = map.getBearing();
                trackingParams.bearing.fn = t => (bearing - (t - now) / 200) % 360;
            } else if (trackingMode === 'helicopter') {
                const bearing = map.getBearing();
                trackingParams.bearing.fn = t => (bearing + (t - now) / 400) % 360;
            }
        }
    }

    updateHandlersAndControls() {
        const me = this,
            {map, trackedObject, trackingMode} = me,
            handlers = ['scrollZoom', 'boxZoom', 'dragRotate', 'dragPan', 'keyboard', 'doubleClickZoom', 'touchZoomRotate', 'touchPitch'];

        if (isVehicle(trackedObject) && trackingMode !== 'position') {
            for (const handler of handlers) {
                map[handler].disable();
            }
            me.navControl.disable();
        } else {
            for (const handler of handlers) {
                map[handler].enable();
            }
            if (isVehicle(trackedObject) && trackingMode === 'position') {
                map.dragPan.disable();
            }
            me.navControl.enable();
        }
    }

    startViewAnimation() {
        const me = this;
        let t2 = 0,
            t4 = 0;

        me.viewAnimationID = animation.start({
            callback: (elapsed, duration) => {
                const trackedObject = me.trackedObject,
                    t1 = easeOutQuart(elapsed / duration),
                    easeOutFactor = 1 - (1 - t1) / (1 - t2),
                    t3 = easeInQuad(elapsed / duration),
                    easeInFactor = 1 - (1 - t3) / (1 - t4);

                me._jumpTo({
                    center: trackedObject.coord,
                    altitude: trackedObject.altitude,
                    bearing: trackedObject.bearing,
                    easeOutFactor,
                    easeInFactor,
                    bearingFactor: easeOutFactor
                });
                t2 = t1;
                t4 = t3;
            },
            complete: () => {
                delete me.viewAnimationID;
            },
            duration: 1000
        });
    }

    stopViewAnimation() {
        const me = this;

        if (me.viewAnimationID) {
            animation.stop(me.viewAnimationID);
            delete me.viewAnimationID;
        }
    }

    /**
     * Returns a LngLat representing geographical coordinates on the ground
     * that shares the same projected pixel coordinates with the specified
     * geographical location and altitude.
     * @param {LngLatLike} coord - The geographical location to adjust
     * @param {number} altitude - The altitude in meters of the position
     * @param {number} bearing - The desired bearing of the camera in degrees.
     *     If not specified, the coordinates will be calculated using the
     *     current camera projection matrix
     * @returns {LngLat} The adjusted LngLat.
     */
    adjustCoord(coord, altitude, bearing) {
        if (!altitude) {
            return LngLat.convert(coord);
        }

        const {map, trafficLayer} = this;

        if (!isNaN(bearing)) {
            const mCoord = MercatorCoordinate.fromLngLat(coord, altitude),
                offset = mCoord.z * Math.tan(map.getPitch() * DEGREE_TO_RADIAN),
                x = mCoord.x + offset * Math.sin(bearing * DEGREE_TO_RADIAN),
                y = mCoord.y - offset * Math.cos(bearing * DEGREE_TO_RADIAN);

            return new MercatorCoordinate(x, y, 0).toLngLat();
        }
        return map.unproject(trafficLayer.project(coord, altitude));
    }

    getLocalizedRailwayTitle(railway) {
        const me = this,
            title = (railway || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedTrainNameOrRailwayTitle(names, railway) {
        const me = this;

        return names ?
            names.map(name => name[me.lang] || name.en).join(me.dict['and']) :
            me.getLocalizedRailwayTitle(railway);
    }

    getLocalizedTrainTypeTitle(type) {
        const me = this,
            title = (type || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedStationTitle(array) {
        const me = this,
            stations = Array.isArray(array) ? array : [array];

        return stations.map(station => {
            const title = (station || {}).title || {};
            return title[me.lang] || title.en;
        }).join(me.dict['and']);
    }

    getLocalizedRailDirectionTitle(direction) {
        const me = this,
            title = (direction || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedDestinationTitle(destination, direction) {
        const me = this;

        return destination ?
            me.dict['for'].replace('$1', me.getLocalizedStationTitle(destination)) :
            me.getLocalizedRailDirectionTitle(direction);
    }

    getLocalizedOperatorTitle(operator) {
        const me = this,
            title = (operator || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedAirportTitle(airport) {
        const me = this,
            title = (airport || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedFlightStatusTitle(status) {
        const me = this,
            title = (status || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedPOITitle(poi) {
        const me = this,
            title = (poi || {}).title || {};

        return title[me.lang] || title.en;
    }

    getLocalizedPOIDescription(poi) {
        const me = this,
            description = (poi || {}).description || {};

        return description[me.lang] || description.en;
    }

    getTrainDescription(train) {
        const me = this,
            {lang, dict} = me,
            {r: railway, departureTime, arrivalStation} = train,
            color = (train.v || railway).color,
            delay = train.delay || 0,
            arrivalTime = helpers.valueOrDefault(train.arrivalTime, train.nextDepartureTime),
            status = railway.status;

        return [
            '<div class="desc-header">',
            Array.isArray(color) ? [
                '<div>',
                ...color.slice(0, 3).map(c => `<div class="line-strip" style="background-color: ${c};"></div>`),
                '</div>'
            ].join('') : `<div style="background-color: ${color};"></div>`,
            '<div><strong>',
            me.getLocalizedTrainNameOrRailwayTitle(train.nm, railway),
            '</strong>',
            `<br> <span class="train-type-label">${me.getLocalizedTrainTypeTitle(train.y)}</span> `,
            me.getLocalizedDestinationTitle(train.ds, train.d),
            '</div></div>',
            `<strong>${dict['train-number']}:</strong> ${train.n}`,
            !train.timetable ? ` <span class="desc-caution">${dict['special']}</span>` : '',
            '<br>',
            delay >= 60000 ? '<span class="desc-caution">' : '',
            '<strong>',
            dict[train.standing ? 'standing-at' : 'previous-stop'],
            ':</strong> ',
            me.getLocalizedStationTitle(train.departureStation),
            departureTime !== undefined ? ` ${helpers.getTimeString(departureTime + delay)}` : '',
            arrivalStation ? [
                `<br><strong>${dict['next-stop']}:</strong> `,
                me.getLocalizedStationTitle(arrivalStation),
                arrivalTime !== undefined ? ` ${helpers.getTimeString(arrivalTime + delay)}` : ''
            ].join('') : '',
            delay >= 60000 ? `<br>${dict['delay'].replace('$1', Math.floor(delay / 60000))}</span>` : '',
            status && lang === 'ja' ? `<br><span class="desc-caution"><strong>${status}:</strong> ${railway.text}</span>` : ''
        ].join('');
    }

    getFlightDescription(flight) {
        const me = this,
            dict = me.dict,
            {a: airline, n: flightNumber, ds: destination} = flight,
            tailcolor = airline.tailcolor || '#FFFFFF',
            scheduledTime = helpers.valueOrDefault(flight.sdt, flight.sat),
            estimatedTime = helpers.valueOrDefault(flight.edt, flight.eat),
            actualTime = helpers.valueOrDefault(flight.adt, flight.aat),
            delayed = (estimatedTime !== undefined || actualTime !== undefined) && scheduledTime !== helpers.valueOrDefault(estimatedTime, actualTime);

        return [
            '<div class="desc-header">',
            `<div style="background-color: ${tailcolor};"></div>`,
            `<div><strong>${me.getLocalizedOperatorTitle(airline)}</strong>`,
            `<br>${flightNumber[0]} `,
            dict[destination ? 'to' : 'from'].replace('$1', me.getLocalizedAirportTitle(destination || flight.or)),
            '</div></div>',
            `<strong>${dict['status']}:</strong> ${me.getLocalizedFlightStatusTitle(flight.s)}`,
            '<br><strong>',
            dict[destination ? 'scheduled-departure-time' : 'scheduled-arrival-time'],
            `:</strong> ${helpers.getTimeString(scheduledTime)}`,
            delayed ? '<span class="desc-caution">' : '',
            estimatedTime !== undefined ? [
                '<br><strong>',
                dict[destination ? 'estimated-departure-time' : 'estimated-arrival-time'],
                `:</strong> ${helpers.getTimeString(estimatedTime)}`
            ].join('') : actualTime !== undefined ? [
                '<br><strong>',
                dict[destination ? 'actual-departure-time' : 'actual-arrival-time'],
                `:</strong> ${helpers.getTimeString(actualTime)}`
            ].join('') : '',
            delayed ? '</span>' : '',
            flightNumber.length > 1 ? `<br><strong>${dict['code-share']}:</strong> ${flightNumber.slice(1).join(' ')}` : ''
        ].join('');
    }

    getBusDescription(bus) {
        const me = this,
            dict = me.dict,
            gtfs = me.gtfs.get(bus.gtfsId),
            stopLookup = gtfs.stopLookup,
            trip = bus.trip,
            {shortName, headsigns, stops} = trip,
            nextStopIndex = bus.sectionIndex + bus.sectionLength,
            nextStopName = stopLookup.get(stops[nextStopIndex]).name,
            prevStopIndex = Math.max(0, nextStopIndex - 1),
            prevStopName = stopLookup.get(stops[prevStopIndex]).name;

        return [
            '<div class="desc-header">',
            `<div style="background-color: ${gtfs.color};"></div>`,
            `<div><strong>${gtfs.agency}</strong><br>`,
            shortName ? ` <span class="bus-route-label" style="color: ${trip.textColor}; background-color: ${trip.color};">${shortName}</span> ` : '',
            headsigns[headsigns.length === 1 ? 0 : prevStopIndex],
            '</div></div>',
            `<strong>${dict['vehicle-number']}:</strong> ${bus.id}`,
            `<br><strong>${dict['previous-busstop']}:</strong> ${prevStopName}`,
            `<br><strong>${dict['next-busstop']}:</strong> ${nextStopName}`
        ].join('');
    }

    /**
     * Check if any of connecting trains is active
     * @param {Object} train - train to check
     * @returns {boolean} True if any of connecting trains is active
     */
    checkActiveTrains(train) {
        const me = this;

        function check(curr, prop) {
            const activeTrain = me.activeTrainLookup.get(curr.t);

            // Need to check timetable ID
            if (activeTrain && curr.id === activeTrain.timetable.id) {
                return true;
            }

            const timetables = curr[prop];

            if (timetables) {
                for (const timetable of timetables) {
                    if (check(timetable, prop)) {
                        return true;
                    }
                }
            }
            return false;
        }

        return train.timetable ? check(train.timetable, 'pt') || check(train.timetable, 'nt') : false;
    }

    stopTrain(train, keep) {
        const me = this,
            cars = train.cars;

        animation.stop(train.animationID);
        if (cars) {
            for (const car of cars) {
                me.trafficLayer.removeObject(car);
                if (car === me.markedObject && !keep) {
                    me.markObject();
                }
                if (car === me.trackedObject && !keep) {
                    me.trackObject();
                }
            }
        }
        delete train.cars;
        me.activeTrainLookup.delete(train.id);
    }

    deactivateTrain(train) {
        const me = this,
            {markedObject, trackedObject} = me;

        if (markedObject && markedObject.object === train) {
            me.markObject();
        }
        if (trackedObject && trackedObject.object === train) {
            me.trackObject();
        }
    }

    stopFlight(flight) {
        const me = this,
            aircraft = flight.aircraft;

        animation.stop(flight.animationID);
        me.trafficLayer.removeObject(aircraft);
        if (aircraft === me.markedObject) {
            me.markObject();
        }
        if (aircraft === me.trackedObject) {
            me.trackObject();
        }
        delete flight.aircraft;
        me.activeFlightLookup.delete(flight.id);
    }

    stopBus(bus) {
        const me = this,
            car = bus.car;

        animation.stop(bus.animationID);
        me.trafficLayer.removeObject(car);
        if (car === me.markedObject) {
            me.markObject();
        }
        if (car === me.trackedObject) {
            me.trackObject();
        }
        delete bus.car;
        me.gtfs.get(bus.gtfsId).activeBusLookup.delete(bus.trip.id);
    }

    stopAll() {
        const me = this;

        for (const train of me.activeTrainLookup.values()) {
            me.stopTrain(train);
        }
        for (const flight of me.activeFlightLookup.values()) {
            me.stopFlight(flight);
        }
        me.standbyTrainLookup.clear();
        me.realtimeTrains.clear();
        for (const {activeBusLookup, realtimeBuses} of me.gtfs.values()) {
            for (const bus of activeBusLookup.values()) {
                me.stopBus(bus);
            }
            realtimeBuses.clear();
        }
        delete me.lastTrainRefresh;

        // アクティブなSafieデリを削除（LocationPanel統合版）
        if (me._safieMarkers) {
            me._safieMarkers.forEach(marker => marker.remove());
            me._safieMarkers = [];
        }
        me.activeSafieDevices = [];

        // Safie定期更新を停止
        if (me.safieUpdateIntervalId) {
            stopSafieDeviceUpdates(me.safieUpdateIntervalId);
            me.safieUpdateIntervalId = null;
        }

        // アクティブなIchimillデバイスを削除
        if (me._ichimillMarkers) {
            me._ichimillMarkers.forEach(marker => marker.remove());
            me._ichimillMarkers = [];
        }
        me.activeIchimillDevices = [];

        // Ichimill定期更新を停止
        if (me.ichimillUpdateIntervalId) {
            stopIchimillDeviceUpdates(me.ichimillUpdateIntervalId);
            me.ichimillUpdateIntervalId = null;
        }

        // アクティブな工事情報を削除
        if (me._koujiUnderConstructionMarkers) {
            me._koujiUnderConstructionMarkers.forEach(marker => marker.remove());
            me._koujiUnderConstructionMarkers = [];
        }
        if (me._koujiCompleteMarkers) {
            me._koujiCompleteMarkers.forEach(marker => marker.remove());
            me._koujiCompleteMarkers = [];
        }
        me.activeKoujiLocations = [];

        // 工事情報定期更新を停止
        if (me.koujiUpdateIntervalId) {
            stopKoujiLocationUpdates(me.koujiUpdateIntervalId);
            me.koujiUpdateIntervalId = null;
        }
    }

    resetRailwayStatus() {
        for (const railway of this.railways.getAll()) {
            delete railway.status;
            delete railway.text;
            delete railway.suspended;
        }
    }

    refreshTrainTimetableData() {
        // リアルタイム公共交通データ更新の停止
        // const me = this;

        // showLoader(me.container);
        // me.timetables.clear();
        // loadTimetableData(me.dataUrl, me.clock).then(data => {
        //     me.timetables = new TrainTimetables(data, me.dataReferences);
        //     delete me.lastTrainRefresh;
        //     hideLoader(me.container);
        // });
        return;
    }

    refreshBusData() {
        // 公共交通機能を無効化のため全体をコメントアウト
        // const me = this;

        // loadBusData(me.dataSources, me.lang).then(data =>
        //     new Promise(resolve => me.initialized ? resolve(data) : me.once('initialized', () => resolve(data)))
        // ).then(data => {
        //     const map = me.map;

        //     for (const {id, activeBusLookup, layerIds} of me.gtfs.values()) {
        //         for (const bus of activeBusLookup.values()) {
        //             me.stopBus(bus);
        //         }

        //         for (const layerId of layerIds) {
        //             me.removeLayer(layerId);
        //             for (let i = 0, ilen = me.styleOpacities.length; i < ilen; i++) {
        //                 if (me.styleOpacities[i].id === layerId) {
        //                     me.styleOpacities.splice(i, 1);
        //                     break;
        //                 }
        //             }
        //         }
        //         map.removeSource(`gtfs-${id}`);

        //         me.gtfs.delete(id);
        //     }

        //     for (const item of data) {
        //         const {agency, featureCollection, version} = item,
        //             id = `${agency}.${version}`,
        //             featureLookup = new Map(),
        //             stopLookup = new Map(),
        //             tripLookup = new Map(),
        //             layerIds = new Set(),
        //             source = `gtfs-${id}`;

        //         featureEach(featureCollection, feature => {
        //             const properties = feature.properties;

        //             if (properties.type === 0) {
        //                 featureLookup.set(properties.id, feature);
        //             }
        //         });
        //         for (const stop of item.stops) {
        //             stopLookup.set(stop.id, stop);
        //         }
        //         for (const trip of item.trips) {
        //             tripLookup.set(trip.id, trip);
        //         }
        //         me.gtfs.set(id, {
        //             id,
        //             agency,
        //             version,
        //             featureLookup,
        //             stopLookup,
        //             tripLookup,
        //             layerIds,
        //             activeBusLookup: new Map(),
        //             realtimeBuses: new Set(),
        //             vehiclePositionUrl: item.vehiclePositionUrl,
        //             color: item.color
        //         });

        //         map.addSource(source, {
        //             type: 'geojson',
        //             data: featureCollection
        //         });

        //         me.addLayer({
        //             id: `busroute-${id}-og-`,
        //             type: 'line',
        //             source,
        //             filter: ['==', ['get', 'type'], 0],
        //             paint: {
        //                 'line-color': ['get', 'color'],
        //                 'line-width': [
        //                     'interpolate',
        //                     ['exponential', 2],
        //                     ['zoom'],
        //                     11,
        //                     ['/', ['get', 'width'], 2],
        //                     12,
        //                     ['get', 'width'],
        //                     19,
        //                     ['get', 'width'],
        //                     22,
        //                     ['*', ['get', 'width'], 8]
        //                 ]
        //             },
        //             metadata: {
        //                 'mt3d:opacity-effect': true,
        //                 'mt3d:opacity': 1,
        //                 'mt3d:opacity-route': 0.1,
        //                 'mt3d:opacity-underground': 0.25,
        //                 'mt3d:opacity-underground-route': 0.1
        //             }
        //         }, 'railways-og-13');
        //         layerIds.add(`busroute-${id}-og-`);

        //         for (const zoom of [14, 15, 16, 17, 18]) {
        //             const interpolate = ['interpolate', ['exponential', 2], ['zoom']],
        //                 width = ['get', 'width'],
        //                 lineWidth = zoom === 18 ? [...interpolate, 19, width, 22, ['*', width, 8]] : width;

        //             for (const key of ['busstops', 'busstops-outline']) {
        //                 me.addLayer({
        //                     id: `${key}-${id}-og-${zoom}`,
        //                     type: key === 'busstops' ? 'fill' : 'line',
        //                     source,
        //                     filter: ['all', ['==', ['get', 'zoom'], zoom], ['==', ['get', 'type'], 1]],
        //                     layout: {
        //                         visibility: zoom === me.layerZoom ? 'visible' : 'none'
        //                     },
        //                     paint: {
        //                         'busstops': {
        //                             'fill-color': ['get', 'color'],
        //                             'fill-opacity': .7
        //                         },
        //                         'busstops-outline': {
        //                             'line-color': ['get', 'outlineColor'],
        //                             'line-width': lineWidth
        //                         }
        //                     }[key],
        //                     metadata: {
        //                         'mt3d:opacity-effect': true,
        //                         'mt3d:opacity': 1,
        //                         'mt3d:opacity-route': 0.1,
        //                         'mt3d:opacity-underground': 0.25,
        //                         'mt3d:opacity-underground-route': 0.1
        //                     }
        //                 }, 'railways-og-13');
        //                 layerIds.add(`${key}-${id}-og-${zoom}`);
        //             }
        //         }

        //         me.addLayer({
        //             id: `busstops-poi-${id}`,
        //             type: 'symbol',
        //             source,
        //             filter: ['==', ['get', 'type'], 2],
        //             layout: {
        //                 'text-field': ['get', 'name'],
        //                 'text-font': [
        //                     'Open Sans Bold',
        //                     'Arial Unicode MS Bold'
        //                 ],
        //                 'text-max-width': 9,
        //                 'text-padding': 2,
        //                 'text-size': 12,
        //                 'text-anchor': 'bottom',
        //                 'text-offset': [0, -1]
        //             },
        //             paint: {
        //                 'text-color': 'rgba(102,102,102,1)',
        //                 'text-halo-blur': 0.5,
        //                 'text-halo-color': 'rgba(255,255,255,1)',
        //                 'text-halo-width': 1
        //             },
        //             minzoom: 14
        //         });
        //         layerIds.add(`busstops-poi-${id}`);
        //     }

        //     const existingLayerIds = new Set(me.styleOpacities.map(({id}) => id));

        //     for (const item of helpersMapbox.getStyleOpacities(map, 'mt3d:opacity-effect')) {
        //         if (!existingLayerIds.has(item.id)) {
        //             me.styleOpacities.push(item);
        //         }
        //     }
        //     me.refreshMap();

        //     if (me.clockMode === 'realtime') {
        //         me.refreshRealtimeBusData();
        //     }
        // });
        return;
    }

    refreshRealtimeTrainData() {
        // 公共交通機能を無効化のため全体をコメントアウト
        // const me = this;

        // loadDynamicTrainData(me.secrets).then(({trainData, trainInfoData}) => {
        //     const {activeTrainLookup, standbyTrainLookup, realtimeTrains, dataReferences} = me,
        //         now = me.clock.getTimeOffset();

        //     me.resetRailwayStatus();

        //     for (const trainInfoRef of trainInfoData) {
        //         const railway = me.railways.get(trainInfoRef.railway),
        //             status = trainInfoRef.status;

        //         // Train information text is provided in Japanese only
        //         if (railway && status && status.ja) {
        //             railway.status = status.ja;
        //             railway.text = trainInfoRef.text.ja;
        //         }

        //         if (trainInfoRef.suspended) {
        //             railway.suspended = true;
        //         }
        //     }

        //     standbyTrainLookup.clear();
        //     realtimeTrains.clear();

        //     for (const trainRef of trainData) {
        //         const {id, r, n, y, d, os, ds, ts, fs, v, delay, carComposition} = trainRef,
        //             aliasId = id.replace('.Marunouchi.', '.MarunouchiBranch.');

        //         me.lastDynamicUpdate[trainRef.o] = trainRef.date;
        //         realtimeTrains.add(trainRef.id);

        //         // Retry lookup replacing Marunouchi line with MarunouchiBranch line
        //         const activeTrain = activeTrainLookup.get(id) || activeTrainLookup.get(aliasId);

        //         // Update the avtive train if exists
        //         if (activeTrain) {
        //             if ((y && y !== activeTrain.y.id) ||
        //                 (os && activeTrain.os && os[0] !== activeTrain.os[0].id) ||
        //                 (ds && activeTrain.ds && ds[0] !== activeTrain.ds[0].id) ||
        //                 (v && v !== activeTrain.v.id) ||
        //                 (!isNaN(carComposition) && carComposition !== activeTrain.carComposition) ||
        //                 (!isNaN(delay) && delay !== activeTrain.delay)) {
        //                 me.stopTrain(activeTrain, true);
        //             } else {
        //                 if (!activeTrain.timetable) {
        //                     activeTrain.update({ts, fs}, dataReferences);
        //                 }
        //                 continue;
        //             }
        //         }

        //         let timetables = me.timetables.getByTrainId(id);

        //         // Retry lookup replacing Marunouchi line with MarunouchiBranch line
        //         if (timetables.length === 0) {
        //             timetables = me.timetables.getByTrainId(aliasId);
        //         }

        //         // Start train with timetable
        //         if (timetables.length !== 0) {
        //             for (const timetable of timetables) {
        //                 const train = new Train(timetable);

        //                 train.update({y, os, ds, v, delay, carComposition}, dataReferences);
        //                 if (timetable.start + (delay || 0) <= now && now <= timetable.end + (delay || 0)) {
        //                     me.trainStart(train);
        //                 } else {
        //                     me.deactivateTrain(train);
        //                     standbyTrainLookup.set(timetable.id, train);
        //                 }
        //             }
        //             continue;
        //         }

        //         if (!r) {
        //             continue;
        //         }

        //         // Exclude Namboku line trains that connect to/from Mita line
        //         if (r === RAILWAY_NAMBOKU && (os[0].startsWith(RAILWAY_MITA) || ds[0].startsWith(RAILWAY_MITA))) {
        //             continue;
        //         }

        //         // Exclude Arakawa line trains
        //         if (r === RAILWAY_ARAKAWA) {
        //             continue;
        //         }

        //         // Start train without timetable
        //         me.trainStart(new Train({id, r, n, y, d, os, ds, ts, fs, delay, carComposition}, dataReferences));
        //     }

        //     // Stop trains if they are no longer active
        //     for (const train of activeTrainLookup.values()) {
        //         const railway = train.r;

        //         if ((((railway.status && railway.dynamic) || !train.timetable) && !realtimeTrains.has(train.id)) || railway.suspended) {
        //             me.stopTrain(train);
        //         }
        //     }

        //     me.refreshTrains();
        //     me.aboutPanel.updateContent();
        // }).catch(error => {
        //     me.refreshTrains();
        //     console.log(error);
        // });
        return;
    }

    refreshRealtimeFlightData() {
        // 公共交通機能を無効化のため全体をコメントアウト
        // const me = this;

        // loadDynamicFlightData(me.secrets).then(({atisData, flightData}) => {
        //     const flightLookup = me.flightLookup,
        //         {landing, departure} = atisData,
        //         pattern = [landing.join('/'), departure.join('/')].join(' '),
        //         codeShareFlights = {},
        //         flightQueue = {};
        //     let arrRoutes = {},
        //         depRoutes = {},
        //         north = true;

        //     if (me.flightPattern !== pattern) {
        //         me.flightPattern = pattern;
        //         me.lastFlightPatternChanged = Date.now();
        //         for (const flight of me.activeFlightLookup.values()) {
        //             me.stopFlight(flight);
        //         }
        //     }

        //     if (helpers.includes(landing, ['R16L', 'R16R'])) { // South wind, good weather, rush hour
        //         arrRoutes = {S: 'R16L', N: 'R16R'};
        //         depRoutes = {S: '22', N: '16R'};
        //         north = false;
        //     } else if (helpers.includes(landing, ['L22', 'L23'])) { // South wind, good weather
        //         arrRoutes = {S: 'L23', N: 'L22'};
        //         depRoutes = {S: 'O16R', N: '16L'};
        //         north = false;
        //     } else if (helpers.includes(landing, ['I16L', 'I16R'])) { // South wind, bad weather, rush hour
        //         arrRoutes = {S: 'I16L', N: 'I16R'};
        //         depRoutes = {S: '22', N: '16R'};
        //         north = false;
        //     } else if (helpers.includes(landing, ['I22', 'I23'])) { // South wind, bad weather
        //         arrRoutes = {S: 'I23', N: 'I22'};
        //         depRoutes = {S: '16R', N: '16L'};
        //         north = false;
        //     } else if (helpers.includes(landing, ['I34L', 'H34R'])) { // North wind, good weather
        //         arrRoutes = {S: 'IX34L', N: 'H34R'};
        //         depRoutes = {S: '05', N: '34R'};
        //         north = true;
        //     } else if (helpers.includes(landing, ['I34L', 'I34R'])) { // North wind, bad weather
        //         arrRoutes = {S: 'IZ34L', N: 'H34R'};
        //         depRoutes = {S: '05', N: '34R'};
        //         north = true;
        //     } else if (landing.length !== 1) {
        //         console.log(`Unexpected RWY: ${landing}`);
        //     } else { // Midnight
        //         if (helpers.includes(landing, 'I23')) {
        //             arrRoutes = {S: 'IY23', N: 'IY23'};
        //             north = false;
        //         } else if (helpers.includes(landing, 'L23')) {
        //             arrRoutes = {S: 'LY23', N: 'LY23'};
        //             north = false;
        //         } else if (helpers.includes(landing, 'I34L')) {
        //             arrRoutes = {S: 'IX34L', N: 'IX34L'};
        //             north = true;
        //         } else if (helpers.includes(landing, 'I34R')) {
        //             arrRoutes = {S: 'IY34R', N: 'IY34R'};
        //             north = true;
        //         } else if (helpers.includes(landing, 'L22')) { // Special
        //             arrRoutes = {S: 'L22', N: 'L22'};
        //             north = false;
        //         } else {
        //             console.log(`Unexpected LDG RWY: ${landing[0]}`);
        //         }
        //         if (helpers.includes(departure, '16L')) {
        //             depRoutes = {S: 'N16L', N: 'N16L'};
        //         } else if (helpers.includes(departure, '05')) {
        //             depRoutes = {S: 'N05', N: 'N05'};
        //         } else if (helpers.includes(departure, '16R')) { // Special
        //             depRoutes = {S: '16R', N: '16R'};
        //         } else {
        //             console.log(`Unexpected DEP RWY: ${departure[0]}`);
        //         }
        //     }

        //     // Create code share flight lookup
        //     for (const flightRef of flightData) {
        //         if (helpers.includes(AIRLINES_FOR_ANA_CODE_SHARE, flightRef.a)) {
        //             const {dp, ds, sdt, or, ar, sat} = flightRef,
        //                 key = `${dp || or}.${ds || ar}.${sdt || sat}`;

        //             codeShareFlights[key] = flightRef;
        //         }
        //     }

        //     for (const flightRef of flightData) {
        //         const {id, n, dp, ds, sdt, or, ar, sat} = flightRef;
        //         let flight = flightLookup.get(id),
        //             status = flightRef.s,
        //             {maxFlightSpeed: maxSpeed, flightAcceleration: acceleration} = configs;

        //         // Check code share flight
        //         if (id.match(/NH\d{4}$/)) {
        //             const key = `${dp || or}.${ds || ar}.${sdt || sat}`,
        //                 codeShareFlight = codeShareFlights[key];

        //             if (codeShareFlight) {
        //                 codeShareFlight.n.push(...n);
        //                 continue;
        //             }
        //         }

        //         if (!flight) {
        //             if (helpers.includes(['Cancelled', 'PostponedTomorrow'], status)) {
        //                 continue;
        //             }
        //             const airport = me.airports.get(ds || or),
        //                 direction = airport ? airport.direction : 'S',
        //                 route = dp === 'NRT' ? `NRT.${north ? '34L' : '16R'}.Dep` :
        //                 ar === 'NRT' ? `NRT.${north ? '34R' : '16L'}.Arr` :
        //                 dp === 'HND' ? `HND.${depRoutes[direction]}.Dep` :
        //                 ar === 'HND' ? `HND.${arrRoutes[direction]}.Arr` : undefined,
        //                 feature = me.featureLookup.get(route);

        //             if (feature) {
        //                 flight = new Flight({
        //                     id,
        //                     n,
        //                     a: flightRef.a,
        //                     dp,
        //                     ar,
        //                     ds,
        //                     or
        //                 }, {
        //                     airports: me.airports,
        //                     operators: me.operators
        //                 });
        //                 flight.runway = route.replace(/^([^.]+\.)[A-Z]*([^.]+).+/, '$1$2');
        //                 flight.feature = feature;
        //                 flightLookup.set(flight.id, flight);
        //             } else {
        //                 continue;
        //             }
        //         }
        //         flight.update({
        //             edt: flightRef.edt,
        //             adt: flightRef.adt,
        //             sdt,
        //             eat: flightRef.eat,
        //             aat: flightRef.aat,
        //             sat
        //         });

        //         const departureTime = helpers.valueOrDefault(flight.edt, helpers.valueOrDefault(flight.adt, flight.sdt)),
        //             arrivalTime = helpers.valueOrDefault(flight.eat, helpers.valueOrDefault(flight.aat, flight.sat));

        //         if (arrivalTime !== undefined && !status) {
        //             if (arrivalTime < flight.sat) {
        //                 status = 'NewTime';
        //             } else if (arrivalTime > flight.sat) {
        //                 status = 'Delayed';
        //             } else if (arrivalTime === flight.sat) {
        //                 status = 'OnTime';
        //             }
        //         } else if (departureTime !== undefined && (!status || status === 'CheckIn' || status === 'NowBoarding' || status === 'FinalCall' || status === 'BoardingComplete' || status === 'Departed')) {
        //             if (departureTime < flight.sdt) {
        //                 status = 'NewTime';
        //             } else if (departureTime > flight.sdt) {
        //                 status = 'Delayed';
        //             } else if (departureTime === flight.sdt) {
        //                 status = 'OnTime';
        //             }
        //         }
        //         flight.update({s: status}, {flightStatuses: me.flightStatuses});

        //         if (arrivalTime !== undefined) {
        //             maxSpeed /= 2;
        //             acceleration /= -2;
        //         }

        //         const duration = maxSpeed / Math.abs(acceleration) / 2 + flight.feature.properties.length / maxSpeed,
        //             standingDuration = configs.standingDuration;

        //         if (departureTime) {
        //             flight.start = flight.base = departureTime;
        //             flight.entry = flight.start - standingDuration;
        //             flight.end = flight.start + duration;
        //         } else {
        //             flight.start = flight.entry = arrivalTime - duration;
        //             flight.base = flight.start + duration - standingDuration;
        //             flight.end = flight.start + duration + standingDuration;
        //         }
        //         flight.maxSpeed = maxSpeed;
        //         flight.acceleration = acceleration;

        //         const queue = flightQueue[flight.runway] = flightQueue[flight.runway] || [];
        //         queue.push(flight);

        //         me.lastDynamicUpdate[flightRef.o] = flightRef.date;
        //     }

        //     for (const key of Object.keys(flightQueue)) {
        //         const queue = flightQueue[key];
        //         let latest = 0;

        //         queue.sort((a, b) => a.base - b.base);
        //         for (const flight of queue) {
        //             const base = flight.base,
        //                 delay = Math.max(base, latest + configs.minFlightInterval) - base;

        //             if (delay) {
        //                 flight.start += delay;
        //                 flight.base += delay;
        //                 flight.entry += delay;
        //                 flight.end += delay;
        //             }
        //             latest = flight.base;
        //         }
        //     }

        //     me.refreshFlights();
        //     me.aboutPanel.updateContent();
        // }).catch(error => {
        //     me.refreshFlights();
        //     console.log(error);
        // });
        return;
    }

    refreshRealtimeBusData() {
        // 公共交通機能を無効化のため全体をコメントアウト
        // const me = this;

        // loadDynamicBusData([...me.gtfs.values()]).then(data => {
        //     for (const {gtfs, vehiclePosition} of data) {
        //         const {id: gtfsId, featureLookup, stopLookup, tripLookup, activeBusLookup, realtimeBuses} = gtfs;

        //         realtimeBuses.clear();
        //         gtfs.date = me.clock.getString(vehiclePosition.header.timestamp * 1000);

        //         for (const {id, vehicle: vp} of vehiclePosition.entity) {
        //             const vehicle = vp.vehicle,
        //                 stop = vp.currentStopSequence,
        //                 position = vp.position,
        //                 tripId = vp.trip && vp.trip.tripId;

        //             if (!(stop || position) || !tripId) {
        //                 continue;
        //             }

        //             const busTrip = tripLookup.get(tripId),
        //                 feature = busTrip && featureLookup.get(busTrip.shape);

        //             if (!busTrip || !feature) {
        //                 continue;
        //             }

        //             const isActive = activeBusLookup.has(tripId);
        //             let bus;

        //             if (isActive) {
        //                 bus = activeBusLookup.get(tripId);
        //             } else {
        //                 let offset = 0;
        //                 const offsets = busTrip.stops.map(stopId =>
        //                     // Use the previous offset to calulate a weight and pick a closer point
        //                     (offset = nearestCloserPointOnLine(feature, stopLookup.get(stopId).coord, offset).properties.location)
        //                 );

        //                 bus = {
        //                     id: vehicle ? vehicle.license_plate || vehicle.id : id,
        //                     gtfsId,
        //                     trip: busTrip,
        //                     feature,
        //                     offsets,
        //                     offset: 0
        //                 };
        //             }
        //             if (stop) {
        //                 bus.stop = stop;
        //             } else {
        //                 const offsets = bus.offsets,
        //                     // Use the current bus.offset to calulate a weight and pick a closer point
        //                     offset = nearestCloserPointOnLine(feature, [position.longitude, position.latitude], bus.offset).properties.location;

        //                 bus.stop = busTrip.stopSequences[offsets.reduce(
        //                     (acc, cur, i) => cur < offset ? Math.min(i + 1, offsets.length - 1) : acc, 0
        //                 )];
        //             }
        //             if (!isActive) {
        //                 me.busStart(bus);
        //             }

        //             realtimeBuses.add(tripId);
        //         }
        //     }

        //     me.aboutPanel.updateContent();
        // }).catch(error => {
        //     console.log(error);
        // });
        return;
    }

    updateUndergroundButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.mapboxgl-ctrl-underground');

        if (button) {
            const classList = button.classList;

            if (mode === 'underground') {
                button.title = dict['exit-underground'];
                classList.add('mapboxgl-ctrl-underground-visible');
            } else {
                button.title = dict['enter-underground'];
                classList.remove('mapboxgl-ctrl-underground-visible');
            }
        }
    }

    updatePlaybackButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.mapboxgl-ctrl-playback');

        if (button) {
            const classList = button.classList;

            if (mode === 'playback') {
                button.title = dict['exit-playback'];
                classList.add('mapboxgl-ctrl-playback-active');
            } else {
                button.title = dict['enter-playback'];
                classList.remove('mapboxgl-ctrl-playback-active');
            }
        }
    }

    updateEcoButton(mode) {
        const {container, dict} = this,
            button = container.querySelector('.mapboxgl-ctrl-eco');

        if (button) {
            const classList = button.classList;

            if (mode === 'eco') {
                button.title = dict['exit-eco'];
                classList.add('mapboxgl-ctrl-eco-active');
            } else {
                button.title = dict['enter-eco'];
                classList.remove('mapboxgl-ctrl-eco-active');
            }
        }
    }

    refreshMap() {
        const me = this,
            {viewMode, searchMode} = me,
            isUndergroundMode = viewMode === 'underground',
            isNotSearchResultMode = searchMode === 'none' || searchMode === 'edit',
            factorKey = `mt3d:opacity${isUndergroundMode ? '-underground' : ''}`;

        helpersMapbox.setStyleOpacities(me.map, me.styleOpacities, isNotSearchResultMode ? factorKey : [`${factorKey}-route`, factorKey]);
        me.trafficLayer.setMode(viewMode, searchMode);
    }

    _setSearchMode(mode) {
        const me = this;

        if (me.searchMode !== mode) {
            me.searchMode = mode;
            me.stopAll();
            for (const plugin of me.plugins) {
                plugin.setVisibility(mode === 'none');
            }
            me.refreshMap();
        }
    }

    _setViewMode(mode) {
        const me = this;

        if (me.viewMode === mode) {
            return;
        }

        me.updateUndergroundButton(mode);
        me.viewMode = mode;
        me.refreshMap();
        me.fire({type: 'viewmode', mode});
    }

    _setTrackingMode(mode) {
        const me = this;

        if (me.trackingMode === mode) {
            return;
        }

        me.trackingMode = mode;
        if (isVehicle(me.trackedObject)) {
            me.updateBaseZoom();
            me.updateTrackingParams(true);
            me.updateHandlersAndControls();
            me.startViewAnimation();
        }
        me.fire({type: 'trackingmode', mode});
    }

    _setClockMode(mode) {
        const me = this;

        if (me.clockMode === mode) {
            return;
        }

        me.updatePlaybackButton(mode);
        me.clockMode = mode;
        me.clock.reset();
        me.onClockChange();
        if (me.clockControl) {
            me.clockCtrl.setMode(mode);
        }
        if (mode === 'playback') {
            me.resetRailwayStatus();
        }
        me.fire({type: 'clockmode', mode});
    }

    _setEcoMode(mode) {
        const me = this;

        if (me.ecoMode === mode) {
            return;
        }

        me.updateEcoButton(mode);
        me.ecoMode = mode;
        me.fire({type: 'ecomode', mode});
    }

    onClockChange() {
        const me = this,
            baseTime = me.clock.getTime('03:00');

        me.stopAll();
        me.markObject();
        me.trackObject();

        if (me.lastTimetableRefresh !== baseTime) {
            me.refreshTrainTimetableData();
            me.lastTimetableRefresh = baseTime;
        }
    }

    /**
     * Returns the light color based on the current date and time.
     * In the playback mode, the time in the simulation clock is used.
     * @returns {Object} Color object
     */
    getLightColor() {
        return helpersMapbox.getSunlightColor(this.map, this.clock.getTime());
    }

    pickObject(point) {
        const me = this,
            {map, layerZoom} = me,
            modes = ['ground', 'underground'];
        let object;

        if (me.viewMode === 'underground') {
            modes.reverse();
        }
        for (const mode of modes) {
            object = me.trafficLayer.pickObject(mode, point);
            if (object) {
                return object;
            }
            if (mode === 'ground') {
                object = map.queryRenderedFeatures(point, {layers: [`stations-og-${layerZoom}`]})[0];
            } else {
                object = pickObject(map, `stations-ug-${layerZoom}`, point);
            }
            if (object) {
                return me.stationGroupLookup.get(object.properties.group);
            }
        }
    }

    markObject(object) {
        const me = this,
            {markedObject, trafficLayer, map, popup} = me;

        if (isEqualObject(markedObject, object)) {
            return;
        }

        if (markedObject) {
            if (isVehicle(markedObject)) {
                markedObject.outline = 0;
                trafficLayer.updateObject(markedObject);
            } else {
                me.removeStationOutline('stations-marked');
            }
            delete me.markedObject;
            if (popup && popup.isOpen()) {
                map.getCanvas().style.cursor = '';
                popup.remove();
            }
        }

        if (object && !object.removing) {
            me.markedObject = object;
            map.getCanvas().style.cursor = 'pointer';

            me.popup = new AnimatedPopup({
                className: helpers.isTouchDevice() ? 'popup-object popup-touch' : 'popup-object',
                closeButton: false,
                closeOnClick: false,
                maxWidth: '300px',
                offset: {
                    top: [0, 10],
                    bottom: [0, -30]
                },
                openingAnimation: {
                    duration: 300,
                    easing: 'easeOutBack'
                }
            });
            me.updatePopup({setHTML: true, addToMap: true});

            if (isVehicle(object)) {
                object.outline = 1;
                trafficLayer.updateObject(object);
            } else {
                me.addStationOutline(object, 'stations-marked');
            }
        }
    }

    refreshSearchTargets() {
        const me = this,
            {locationPanel, searchTargetDatalist, searchTargetLookup, activeSafieDevices, activeKoujiLocations} = me;

        if (!searchTargetDatalist || !searchTargetLookup) {
            return;
        }

        searchTargetDatalist.innerHTML = '';
        searchTargetLookup.clear();

        const appendSearchTarget = (label, target) => {
            const normalizedLabel = helpers.normalize((label || '').trim());

            if (!normalizedLabel) {
                return;
            }

            const key = normalizedLabel.toUpperCase();

            if (!searchTargetLookup.has(key)) {
                helpers.createElement('option', {value: normalizedLabel}, searchTargetDatalist);
                searchTargetLookup.set(key, target);
            }
        };

        if (me.isLocationSearchEnabled('safie-cameras')) {
            for (const device of activeSafieDevices || []) {
                appendSearchTarget(device.name, {type: 'safie', object: device});
            }
        }

        for (const location of activeKoujiLocations || []) {
            const locationId = location.status === 'COMPLETE' ? 'kouji-complete' : 'kouji-under-construction';

            if (me.isLocationSearchEnabled(locationId)) {
                appendSearchTarget(location.koujiName || location.name, {type: 'kouji', object: location});
            }
        }
    }

    isLocationSearchEnabled(locationId) {
        const me = this,
            {locationPanel} = me;

        if (!locationPanel || typeof locationPanel._getLocationItem !== 'function') {
            return true;
        }

        const item = locationPanel._getLocationItem(locationId);

        return item ? Boolean(item.enabled) : true;
    }

    selectSearchTarget(value) {
        const me = this,
            normalizedValue = helpers.normalize((value || '').trim());

        if (!normalizedValue || !me.searchTargetLookup) {
            return false;
        }

        return me.moveToSearchTarget(me.searchTargetLookup.get(normalizedValue.toUpperCase()));
    }

    moveToSearchTarget(target) {
        const me = this,
            {locationPanel, map} = me;

        if (!target) {
            return false;
        }

        const {object, type} = target;

        if (!object || !object.coord) {
            return false;
        }

        if (type === 'safie' && !me.isLocationSearchEnabled('safie-cameras')) {
            return false;
        }

        if (type === 'kouji') {
            const locationId = object.status === 'COMPLETE' ? 'kouji-complete' : 'kouji-under-construction';

            if (!me.isLocationSearchEnabled(locationId)) {
                return false;
            }
        }

        me.restoreCameraAfterTyphoonDisplay();
        me.markObject();
        me.trackObject();
        map.flyTo({
            center: object.coord,
            zoom: Math.max(map.getZoom(), type === 'safie' ? 18 : 16.5),
            pitch: Math.max(map.getPitch(), 45)
        });

        if (locationPanel) {
            if (type === 'safie') {
                locationPanel._showSafiePopup(me, object, object.coord);
            } else if (type === 'kouji') {
                locationPanel._showKoujiPopup(me, object, object.coord);
            }
        }

        return true;
    }

    trackObject(object) {
        const me = this,
            {searchMode, lang, map, trackedObject, lastCameraParams, sharePanel, detailPanel} = me;

        if (searchMode === 'edit' && detailPanel && isStation(object)) {
            const {title, utitle} = object.stations[0],
                stationTitle = (utitle && utitle[lang]) || helpers.normalize(title[lang] || title.en);

            detailPanel.fillStationName(stationTitle);
            return;
        }

        if (isEqualObject(trackedObject, object)) {
            if ((isVehicle(object) || isStation(object)) && me.detailPanel) {
                me.detailPanel.reset();
            }
            return;
        }

        if (object) {
            me.restoreCameraAfterTyphoonDisplay({
                instant: true,
                skipTrackObject: true
            });
        }

        // Remember the camera params to restore them after the object is deselected
        if (!trackedObject && object) {
            lastCameraParams.viewMode = me.getViewMode();
            delete lastCameraParams.center;
            lastCameraParams.zoom = map.getZoom();
            lastCameraParams.bearing = map.getBearing();
            lastCameraParams.pitch = map.getPitch();
        } else if (trackedObject && !object) {
            me._setViewMode(lastCameraParams.viewMode);
            map.flyTo({
                center: lastCameraParams.center || map.getCenter(),
                zoom: lastCameraParams.zoom,
                bearing: lastCameraParams.bearing,
                pitch: lastCameraParams.pitch
            });
        }

        if (trackedObject) {
            if (isVehicle(trackedObject)) {
                const prevObject = trackedObject.object;

                trackedObject.outline = 0;
                me.trafficLayer.updateObject(trackedObject);
                me.fire({type: 'deselection', deselection: prevObject.id});
            } else if (isStation(trackedObject)) {
                me.removeStationOutline('stations-selected');
                me.fire({type: 'deselection', deselection: trackedObject.stations.map(({id}) => id)});
                me._setSearchMode('none');
                me.hideStationExits();
            } else {
                me.fire(Object.assign({type: 'deselection'}, trackedObject));
            }
            delete me.trackedObject;
            me.updateHandlersAndControls();
            me.stopViewAnimation();
            if (sharePanel) {
                sharePanel.remove();
                delete me.sharePanel;
            }
            if (detailPanel) {
                detailPanel.remove();
                delete me.detailPanel;
            }
        }

        if (object && !object.removing) {
            me.trackedObject = object;

            if (isVehicle(object)) {
                const _object = object.object;

                me.updateBaseZoom();
                me.updateTrackingParams(true);
                me.updateHandlersAndControls();
                me.startViewAnimation();
                me._setViewMode(object.altitude < 0 ? 'underground' : 'ground');

                if (helpers.includes(['train', 'flight'], object.type) && me.clockMode === 'realtime' && navigator.share) {
                    me.sharePanel = new SharePanel({object: _object});
                    me.sharePanel.addTo(me);
                }
                if (_object.timetable) {
                    me.detailPanel = new TrainPanel({object: _object});
                    me.detailPanel.addTo(me);
                } else if (_object.trip) {
                    me.detailPanel = new BusPanel({object: _object});
                    me.detailPanel.addTo(me);
                }

                object.outline = 1;
                me.trafficLayer.updateObject(object);
                me.fire({type: 'selection', selection: _object.id});
            } else if (isStation(object)) {
                const stations = object.stations.concat(object.hidden || []),
                    coords = stations.map(station => station.coord),
                    center = lastCameraParams.center = helpersMapbox.getBounds(coords).getCenter();

                if (object.layer === 'ground') {
                    me._setViewMode('ground');
                } else if (!map.getCenter().distanceTo(center)) {
                    me._setViewMode('underground');
                } else {
                    let prevZoom = map.getZoom();
                    const initialZoom = prevZoom,
                        onZoom = () => {
                            const zoom = map.getZoom();

                            if (zoom < prevZoom && zoom < initialZoom - 0.5) {
                                me._setViewMode('ground');
                            } else if (zoom > prevZoom && zoom > 15) {
                                me._setViewMode('underground');
                            }
                            prevZoom = zoom;
                        };

                    map.on('zoom', onZoom);
                    map.once('zoomend', () => {
                        me._setViewMode('underground');
                        map.off('zoom', onZoom);
                    });
                }
                map.flyTo({center, zoom: 15.5});

                me.detailPanel = new StationPanel({object: stations});
                me.detailPanel.addTo(me);

                me.addStationOutline(object, 'stations-selected');
                me.fire({type: 'selection', selection: object.stations.map(({id}) => id)});
            } else {
                me.fire(Object.assign({type: 'selection'}, object));
            }
        }
    }

    showStationExits(stations) {
        const me = this,
            map = me.map,
            exits = [].concat(...stations.map(station => station.exit || []));

        if (exits.length > 0) {
            const coords = [];

            me.exitPopups = exits.map((poi, index) => {
                const {coord, facilities = []} = poi,
                    icons = facilities.map(facility => `<span class="exit-${facility}-small-icon"></span>`).join(''),
                    listener = () => {
                        me.exitPopups[index] = setTimeout(() => {
                            const popup = new AnimatedPopup({
                                className: 'popup-exit',
                                closeButton: false,
                                closeOnClick: false
                            });

                            popup.setLngLat(coord)
                                .setHTML(icons + me.getLocalizedPOITitle(poi))
                                .addTo(map)
                                .getElement().id = `exit-${index}`;

                            me.exitPopups[index] = popup;
                        }, index / exits.length * 1000);
                    };

                map.once('moveend', listener);
                coords.push(coord);

                return listener;
            });

            map.fitBounds(helpersMapbox.getBounds(coords), {
                bearing: map.getBearing(),
                pitch: map.getPitch(),
                offset: [0, -map.transform.height / 12],
                padding: {top: 20, bottom: 20, left: 10, right: 50},
                maxZoom: 18
            });
        }
    }

    hideStationExits() {
        const me = this;

        for (const popup of me.exitPopups) {
            if (popup instanceof AnimatedPopup) {
                popup.remove();
            } else if (typeof popup === 'function') {
                me.map.off('moveend', popup);
            } else {
                clearTimeout(popup);
            }
        }
        me.exitPopups = [];
    }

    updateBaseZoom() {
        const me = this,
            {map, trackedObject: object} = me;

        if (isVehicle(object)) {
            const objectZ = me.getModelPosition(object.coord, object.altitude).z,
                cameraZ = map.getFreeCameraOptions().position.z;

            me.baseZoom = map.getZoom() + Math.log2(cameraZ / Math.abs(cameraZ - objectZ));
        }
    }

    updatePopup(options) {
        const me = this,
            {markedObject, map, popup} = me,
            {setHTML, addToMap} = options || {};

        if (isVehicle(markedObject)) {
            const bearing = markedObject === me.trackedObject ? map.getBearing() : undefined;

            popup.setLngLat(me.adjustCoord(markedObject.coord, markedObject.altitude, bearing));
            if (setHTML) {
                popup.setHTML(
                    markedObject.type === 'train' ? me.getTrainDescription(markedObject.object) :
                    markedObject.type === 'flight' ? me.getFlightDescription(markedObject.object) :
                    me.getBusDescription(markedObject.object)
                );
            }
        } else {
            const object = me.featureLookup.get(`${markedObject.id}.${me.layerZoom}`),
                coord = helpersGeojson.getCenterCoord(object),
                altitude = helpersGeojson.getAltitude(object);

            popup.setLngLat(me.adjustCoord(coord, altitude));
            if (setHTML) {
                const stations = markedObject.stations,
                    railwayColors = {};

                for (const station of stations) {
                    const title = me.getLocalizedStationTitle(station),
                        railway = station.railway,
                        colors = railwayColors[title] = railwayColors[title] || {};

                    colors[me.getLocalizedRailwayTitle(railway)] = railway.color;
                }
                popup.setHTML([
                    '<div class="thumbnail-image-container">',
                    '<div class="ball-pulse"><div></div><div></div><div></div></div>',
                    `<div class="thumbnail-image" style="background-image: url(\'${stations[0].thumbnail}\');"></div>`,
                    '</div>',
                    '<div class="railway-list">',
                    Object.keys(railwayColors).map(stationTitle => {
                        const railwayTitles = Object.keys(railwayColors[stationTitle])
                            .map(railwayTitle => `<div class="line-strip" style="background-color: ${railwayColors[stationTitle][railwayTitle]};"></div><span>${railwayTitle}</span>`)
                            .join('<br>');

                        return `<strong>${stationTitle}</strong><br>${railwayTitles}`;
                    }).join('<br>'),
                    '</div>'
                ].join(''));
            }
        }
        if (addToMap) {
            popup.addTo(map);
        }
    }

    addStationOutline(object, name) {
        const me = this,
            id = object.stations[0].id;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(me.map, `${name}-${zoom}`, {
                data: helpersGeojson.featureFilter(me.featureCollection, p => p.zoom === zoom && p.ids && p.ids[0] === id),
                opacity: 1,
                visible: true
            });
        }
    }

    removeStationOutline(name) {
        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(this.map, `${name}-${zoom}`, {
                visible: false
            });
        }
    }

    refreshStationOutline() {
        const p = performance.now() % 1500 / 1500 * 2;

        for (const zoom of [13, 14, 15, 16, 17, 18]) {
            helpersMapbox.setLayerProps(this.map, `stations-selected-${zoom}`, {
                opacity: p < 1 ? p : 2 - p,
                visible: true
            });
        }
    }

    setSectionData(train, index, final) {
        const me = this,
            stations = train.r.stations,
            {direction, timetable} = train,
            destination = (train.ds || [])[0],
            delay = train.delay || 0,
            now = me.clock.getTimeOffset();
        let arrivalTimes, departureTimes, ttIndex, departureStation, arrivalStation, currentSection, nextSection, finalSection;

        if (timetable) {
            const s = timetable.stations;

            arrivalTimes = timetable.arrivalTimes;
            departureTimes = timetable.departureTimes;
            ttIndex = helpers.valueOrDefault(index, departureTimes.reduce(
                (acc, cur, i) => cur !== undefined && cur + delay <= now ? i : acc, 0
            ));
            departureStation = s[ttIndex];
            arrivalStation = s[ttIndex + 1];
        } else {
            departureStation = train.fs || train.ts;
            arrivalStation = train.ts || train.fs;
        }

        if (direction > 0) {
            currentSection = stations.indexOf(departureStation);
            nextSection = stations.indexOf(arrivalStation, currentSection);
            finalSection = stations.indexOf(destination, currentSection);
            if (finalSection === -1) {
                finalSection = stations.length - 1;
            }
        } else {
            currentSection = stations.lastIndexOf(departureStation);
            nextSection = stations.lastIndexOf(arrivalStation, currentSection);
            finalSection = stations.lastIndexOf(destination, currentSection);
            if (finalSection === -1) {
                finalSection = 0;
            }
        }

        if (timetable) {
            train.timetableIndex = ttIndex;
            train.departureStation = departureStation;
            train.departureTime = helpers.valueOrDefault(departureTimes[ttIndex], arrivalTimes[ttIndex]);

            if (currentSection >= 0 && nextSection >= 0) {
                train.sectionIndex = currentSection;
                train.sectionLength = nextSection - currentSection;
                train.arrivalStation = arrivalStation;
                train.arrivalTime = arrivalTimes[ttIndex + 1];
                train.nextDepartureTime = departureTimes[ttIndex + 1];

                return true;
            }

        } else {
            const actualSection = helpers.numberOrDefault(train.sectionIndex + train.sectionLength, currentSection);

            train.departureStation = departureStation;

            if (actualSection >= 0 && actualSection !== finalSection && ((!final && nextSection >= 0) || (final && finalSection >= 0))) {
                train.sectionIndex = actualSection;
                train.sectionLength = (final ? finalSection : nextSection) - actualSection;
                train.arrivalStation = arrivalStation === departureStation ? stations[actualSection + direction] : arrivalStation;

                return true;
            }
        }

        train.arrivalStation = train.arrivalTime = train.nextDepartureTime = undefined;
    }

    setBusSectionData(bus, final) {
        const stopSequences = bus.trip.stopSequences,
            currentSection = stopSequences.indexOf(bus.stop);

        // Guard for an unexpected error
        // Potential data quality issue
        if (currentSection === -1) {
            console.log('no bus stop', bus);
        }

        const finalSection = stopSequences.length - 1,
            nextSection = Math.min(currentSection + 1, finalSection),
            actualSection = helpers.numberOrDefault(bus.sectionIndex + bus.sectionLength, currentSection);

        bus.sectionIndex = actualSection;
        bus.sectionLength = nextSection - actualSection;

        return !final && actualSection !== finalSection;
    }
}

function initContainer(container) {
    const map = helpers.createElement('div', {
        id: 'map'
    }, container);

    helpers.createElement('div', {
        id: 'loader',
        className: 'loader-inner ball-pulse',
        innerHTML: '<div></div><div></div><div></div>'
    }, container);
    helpers.createElement('div', {
        id: 'loading-error'
    }, container);
    container.classList.add('mini-tokyo-3d');
    return map;
}

function showLoader(container) {
    const element = container.querySelector('#loader');

    element.style.opacity = 1;
    element.style.display = 'block';
}

function hideLoader(container) {
    const element = container.querySelector('#loader');

    element.style.opacity = 0;
    setTimeout(() => {
        element.style.display = 'none';
    }, 1000);
}

function showErrorMessage(container) {
    const loaderElement = container.querySelector('#loader'),
        errorElement = container.querySelector('#loading-error');

    loaderElement.style.display = 'none';
    errorElement.innerHTML = 'Loading failed. Please reload the page.';
    errorElement.style.display = 'block';
}

function startTrainAnimation(callback, endCallback, distance, minDuration, maxDuration, start, clock) {
    let {maxSpeed, acceleration, maxAccelerationTime, maxAccDistance} = configs,
        duration, accelerationTime;

    if (distance <= maxAccDistance * 2) {
        duration = Math.sqrt(distance / acceleration) * 2;
        accelerationTime = duration / 2;
    } else {
        duration = maxAccelerationTime * 2 + (distance - maxAccDistance * 2) / maxSpeed;
        if (maxDuration > 0) {
            duration = helpers.clamp(duration, minDuration || 0, maxDuration);
            maxAccDistance = acceleration * duration * duration / 8;
            if (distance >= maxAccDistance * 2) {
                maxSpeed = distance * 2 / duration;
                acceleration = maxSpeed * 2 / duration;
            } else {
                maxSpeed = acceleration * duration / 2 - Math.sqrt(acceleration * (maxAccDistance * 2 - distance));
            }
        }
        accelerationTime = maxSpeed / acceleration;
    }

    return animation.start({
        callback: elapsed => {
            const left = duration - elapsed;
            let d;

            if (elapsed <= accelerationTime) {
                d = acceleration / 2 * elapsed * elapsed;
            } else if (left <= accelerationTime) {
                d = distance - acceleration / 2 * left * left;
            } else {
                d = maxSpeed * (elapsed - accelerationTime / 2);
            }
            callback(d / distance);
        },
        complete: endCallback,
        duration,
        start: start > 0 ? clock.getHighResTime() - start : undefined,
        clock
    });
}

function startFlightAnimation(callback, endCallback, distance, maxSpeed, acceleration, start, clock) {
    const accelerationTime = maxSpeed / Math.abs(acceleration),
        duration = accelerationTime / 2 + distance / maxSpeed;

    return animation.start({
        callback: elapsed => {
            const left = duration - elapsed;
            let d;

            if (acceleration > 0) {
                if (elapsed <= accelerationTime) {
                    d = acceleration / 2 * elapsed * elapsed;
                } else {
                    d = maxSpeed * (elapsed - accelerationTime / 2);
                }
            } else {
                if (left <= accelerationTime) {
                    d = distance + acceleration / 2 * left * left;
                } else {
                    d = maxSpeed * elapsed;
                }
            }
            callback(d / distance);
        },
        complete: endCallback,
        duration,
        start: start > 0 ? clock.getHighResTime() - start : undefined,
        clock
    });
}

function startBusAnimation(callback, endCallback, distance, start, clock) {
    const {maxBusSpeed, busAcceleration, maxBusAccelerationTime, maxBusAccDistance} = configs;
    const duration = distance < maxBusAccDistance * 2 ?
            Math.sqrt(distance / busAcceleration) * 2 :
            maxBusAccelerationTime * 2 + (distance - maxBusAccDistance * 2) / maxBusSpeed,
        accelerationTime = Math.min(maxBusAccelerationTime, duration / 2);

    return animation.start({
        callback: elapsed => {
            const left = duration - elapsed;
            let d;

            if (elapsed <= accelerationTime) {
                d = busAcceleration / 2 * elapsed * elapsed;
            } else if (left <= accelerationTime) {
                d = distance - busAcceleration / 2 * left * left;
            } else {
                d = maxBusSpeed * (elapsed - accelerationTime / 2);
            }
            callback(d / distance);
        },
        complete: endCallback,
        duration,
        start: start > 0 ? clock.getHighResTime() - start : undefined,
        clock
    });
}

function easeInQuad(t) {
    return t * t;
}

function easeOutQuart(t) {
    return -((t -= 1) * t * t * t - 1);
}

function createInterpolant(xs, ys) {
    const length = xs.length;

    // Get consecutive differences and slopes
    const dys = [], dxs = [], ms = [];
    for (let i = 0; i < length - 1; i++) {
        const dx = xs[i + 1] - xs[i], dy = ys[i + 1] - ys[i];
        dxs.push(dx); dys.push(dy); ms.push(dy / dx);
    }

    // Get degree-1 coefficients
    const c1s = [ms[0]];
    for (let i = 0; i < dxs.length - 1; i++) {
        const m = ms[i], mNext = ms[i + 1];
        if (m * mNext <= 0) {
            c1s.push(0);
        } else {
            const dx_ = dxs[i], dxNext = dxs[i + 1], common = dx_ + dxNext;
            c1s.push(3 * common / ((common + dxNext) / m + (common + dx_) / mNext));
        }
    }
    c1s.push(ms[ms.length - 1]);

    // Get degree-2 and degree-3 coefficients
    const c2s = [], c3s = [];
    for (let i = 0; i < c1s.length - 1; i++) {
        const c1 = c1s[i], m_ = ms[i], invDx = 1 / dxs[i], common_ = c1 + c1s[i + 1] - m_ - m_;
        c2s.push((m_ - c1 - common_) * invDx); c3s.push(common_ * invDx * invDx);
    }

    // Return interpolant function
    return x => {
        // The rightmost point in the dataset should give an exact result
        let i = xs.length - 1;
        if (x === xs[i]) {
            return ys[i];
        }

        // Search for the interval x is in, returning the corresponding y if x is one of the original xs
        let low = 0, mid, high = c3s.length - 1;
        while (low <= high) {
            mid = Math.floor(0.5 * (low + high));
            const xHere = xs[mid];
            if (xHere < x) {
                low = mid + 1;
            } else if (xHere > x) {
                high = mid - 1;
            } else {
                return ys[mid];
            }
        }
        i = Math.max(0, high);

        // Interpolate
        const diff = x - xs[i], diffSq = diff * diff;
        return ys[i] + c1s[i] * diff + c2s[i] * diffSq + c3s[i] * diff * diffSq;
    };
}

function getLayerZoom(zoom) {
    return helpers.clamp(Math.floor(zoom), 13, 18);
}

// Deprecated
// function getObjectScale(zoom) {
//     return Math.pow(2, 14 - helpers.clamp(zoom, 13, 19));
// }

function isVehicle(object) {
    return object && helpers.includes(['train', 'flight', 'bus'], object.type);
}

function isStation(object) {
    return object && object.type === 'station';
}

function isEqualObject(a, b) {
    if (a === b) {
        return true;
    }
    if (a && a.properties && b && b.properties && a.properties.ids === b.properties.ids) {
        return true;
    }
    return false;
}
